import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icons using simple text/symbols for Tally style
const Icons = {
  gateway: "⌂",
  students: "👥",
  attendance: "✓",
  fees: "₹",
  receipt: "📄",
  notifications: "✉",
  reports: "📊",
  campuses: "🏫",
  settings: "⚙",
  search: "🔍",
  add: "+",
  edit: "✎",
  delete: "×",
  print: "🖨",
  send: "➤",
  refresh: "↻",
  logout: "⏻",
  user: "👤"
};

// ==================== LOGIN SCREEN ====================
function LoginScreen({ onLogin }) {
  const [role, setRole] = useState("admin");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const roles = [
    { id: "superadmin", label: "Super Admin", icon: "👑" },
    { id: "admin", label: "Admin", icon: "👔" },
    { id: "accountant", label: "Accountant", icon: "💰" },
    { id: "teacher", label: "Teacher", icon: "📚" }
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        user_id: userId,
        password: password,
        role: role
      });
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      onLogin(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-overlay" data-testid="login-screen">
      <div className="login-pattern"></div>
      <div className="login-box">
        <div className="login-title-bar">
          <span>🔐 VIDYALAYA Login</span>
          <span>v3.0</span>
        </div>
        <div className="login-content">
          <div className="login-brand">
            <div className="login-logo">V</div>
            <h1>VIDYALAYA</h1>
            <p>School Management System v3.0</p>
          </div>

          <div className="role-section">
            <label>भूमिका चुनें (Select Role):</label>
            <div className="role-grid">
              {roles.map((r) => (
                <div
                  key={r.id}
                  data-testid={`role-${r.id}`}
                  className={`role-btn ${role === r.id ? "selected" : ""}`}
                  onClick={() => setRole(r.id)}
                >
                  <div className="role-icon">{r.icon}</div>
                  <div>{r.label}</div>
                </div>
              ))}
            </div>
          </div>

          <form className="login-form" onSubmit={handleLogin}>
            <div className="login-field">
              <label>User ID:</label>
              <input
                type="text"
                className="login-input"
                data-testid="login-userid"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter User ID"
                required
              />
            </div>
            <div className="login-field">
              <label>Password:</label>
              <input
                type="password"
                className="login-input"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter Password"
                required
              />
            </div>
            <button
              type="submit"
              className="login-btn"
              data-testid="login-submit"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login [Enter]"}
            </button>
          </form>

          {error && <div className="login-error" data-testid="login-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN APP ====================
function MainApp({ user, onLogout }) {
  const [activePanel, setActivePanel] = useState("gateway");
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState({});
  const [campuses, setCampuses] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState("main");
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);

  // Get user's campus name
  const getUserCampusName = () => {
    if (user.role === "superadmin") {
      return settings.school_name || "VIDYALAYA";
    }
    // For campus users, find their campus name
    if (user.campus_id && campuses.length > 0) {
      const userCampus = campuses.find(c => c.id === user.campus_id);
      if (userCampus) {
        return userCampus.name;
      }
    }
    return settings.school_name || "VIDYALAYA";
  };

  // Filters
  const [filters, setFilters] = useState({
    class: "",
    section: "",
    search: "",
    date: new Date().toISOString().split("T")[0],
    month: new Date().toISOString().slice(0, 7),
    feeStatus: ""
  });

  const token = localStorage.getItem("token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch Data
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { headers: authHeaders });
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.class) params.append("class_name", filters.class);
      if (filters.section) params.append("section", filters.section);
      if (filters.search) params.append("search", filters.search);
      
      const res = await axios.get(`${API}/students?${params}`, { headers: authHeaders });
      setStudents(res.data);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    }
  }, [filters.class, filters.section, filters.search]);

  const fetchFees = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.feeStatus) params.append("status", filters.feeStatus);
      
      const res = await axios.get(`${API}/fees?${params}`, { headers: authHeaders });
      setFees(res.data);
    } catch (err) {
      console.error("Failed to fetch fees:", err);
    }
  }, [filters.feeStatus]);

  const fetchAttendance = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("date", filters.date);
      if (filters.class) params.append("class_name", filters.class);
      if (filters.section) params.append("section", filters.section);
      
      const res = await axios.get(`${API}/attendance?${params}`, { headers: authHeaders });
      setAttendance(res.data);
    } catch (err) {
      console.error("Failed to fetch attendance:", err);
    }
  }, [filters.date, filters.class, filters.section]);

  const fetchReceipts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/receipts`, { headers: authHeaders });
      setReceipts(res.data);
    } catch (err) {
      console.error("Failed to fetch receipts:", err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers: authHeaders });
      setNotifications(res.data);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers: authHeaders });
      setSettings(res.data);
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    }
  }, []);

  const fetchCampuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campuses`, { headers: authHeaders });
      setCampuses(res.data);
    } catch (err) {
      console.error("Failed to fetch campuses:", err);
    }
  }, []);

  const seedData = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/seed`, {}, { headers: authHeaders });
      showToast("Demo data seeded successfully!");
      fetchStats();
      fetchStudents();
      fetchFees();
    } catch (err) {
      showToast(err.response?.data?.message || "Seed failed", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCampuses();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activePanel === "students") fetchStudents();
    if (activePanel === "fees") fetchFees();
    if (activePanel === "attendance") fetchAttendance();
    if (activePanel === "receipt") fetchReceipts();
    if (activePanel === "notifications") fetchNotifications();
  }, [activePanel, fetchStudents, fetchFees, fetchAttendance, fetchReceipts, fetchNotifications]);

  // Navigation items grouped into two rows for responsive display
  // Row 1: Gateway, Students, +New Student, Attendance, Attendance Report
  // Row 2: Fee Deposit, Fee Structure, Fee Report, +New Fee, Receipt, Refresh
  const primaryNav = [
    { id: "gateway", label: "Gateway", icon: Icons.gateway, shortcut: "F1" },
    { id: "students", label: "Students", icon: Icons.students, shortcut: "F2" },
    { id: "attendance", label: "Attendance", icon: Icons.attendance, shortcut: "F3" },
    { id: "attendance-report", label: "Attendance Report", icon: Icons.reports, shortcut: "F12" },
  ];
  const secondaryNav = [
    { id: "fees", label: "Fee Deposit", icon: Icons.fees, shortcut: "F4" },
    { id: "fee-structure", label: "Fee Structure", icon: "₹", shortcut: "F10" },
    { id: "fee-report", label: "Fee Report", icon: Icons.reports, shortcut: "F11" },
    { id: "receipt", label: "Receipt", icon: Icons.receipt, shortcut: "F5" },
  ];
  const adminNav = [
    { id: "notifications", label: "Notifications", icon: Icons.notifications, shortcut: "F6" },
    { id: "notif-history", label: "Notif History", icon: "📜", shortcut: "" },
    { id: "campuses", label: "Campuses", icon: Icons.campuses, shortcut: "F8" },
    { id: "settings", label: "Settings", icon: Icons.settings, shortcut: "F9" },
  ];
  const allNavItems = [...primaryNav, ...secondaryNav, ...adminNav];

  // Filter nav items based on role - teacher can't see fees, receipt, reports, campuses, settings
  const isTeacher = user.role === "teacher";
  const filteredPrimary = isTeacher
    ? primaryNav.filter(i => !["attendance-report"].includes(i.id))
    : primaryNav;
  const filteredSecondary = isTeacher ? [] : secondaryNav;
  const filteredAdmin = isTeacher
    ? []
    : adminNav.filter(i => user.role === "superadmin" || !["campuses"].includes(i.id));
  const navItems = [...filteredPrimary, ...filteredSecondary, ...filteredAdmin];

  return (
    <div className="app-shell" data-testid="main-app">
      {/* Title Bar */}
      <div className="title-bar">
        <div className="title-brand">
          <div className="title-logo">V</div>
          <span>VIDYALAYA — {getUserCampusName()}</span>
        </div>
        <div className="title-info">
          <span>Session: 2026-27</span>
          <span>{user.name} ({user.role})</span>
        </div>
      </div>

      {/* Menu Bar - Row 1 (Attendance & Student Ops) */}
      <div className="menu-bar" data-testid="menu-bar-row1">
        {filteredPrimary.map((item, idx) => {
          const insertNewStudent = idx === 1; // after Students
          return (
            <React.Fragment key={item.id}>
              <div
                className={`menu-item ${activePanel === item.id ? "active" : ""}`}
                onClick={() => setActivePanel(item.id)}
                data-testid={`menu-${item.id}`}
              >
                <span className="underline">{item.label.charAt(0)}</span>
                {item.label.slice(1)}
              </div>
              {insertNewStudent && (
                <div
                  className="menu-item menu-item-action"
                  onClick={() => setModal({
                    type: "addStudent",
                    prefill: activePanel === "students" ? { class_name: filters.class, section: filters.section } : {}
                  })}
                  data-testid="menu-new-student"
                >
                  {Icons.add} New Student
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Menu Bar - Row 2 (Fee Ops & Utilities) */}
      {filteredSecondary.length > 0 && (
        <div className="menu-bar menu-bar-row2" data-testid="menu-bar-row2">
          {filteredSecondary.map((item, idx) => {
            const insertNewFee = idx === 2; // after Fee Report
            return (
              <React.Fragment key={item.id}>
                <div
                  className={`menu-item ${activePanel === item.id ? "active" : ""}`}
                  onClick={() => setActivePanel(item.id)}
                  data-testid={`menu-${item.id}`}
                >
                  <span className="underline">{item.label.charAt(0)}</span>
                  {item.label.slice(1)}
                </div>
                {insertNewFee && user.role !== "teacher" && (
                  <div
                    className="menu-item menu-item-action"
                    onClick={() => setModal({ type: "addFee" })}
                    data-testid="menu-new-fee"
                  >
                    {Icons.add} New Fee
                  </div>
                )}
              </React.Fragment>
            );
          })}
          <div
            className="menu-item menu-item-action"
            onClick={() => { fetchStats(); showToast("Refreshed!"); }}
            data-testid="menu-refresh"
          >
            {Icons.refresh} Refresh
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar" data-testid="toolbar">
        {user.role === "superadmin" && (
          <>
            <button className="toolbar-btn" onClick={seedData} data-testid="seed-btn">
              {Icons.add} Seed Demo Data
            </button>
            <div className="toolbar-sep"></div>
          </>
        )}
        {filteredAdmin.map((item) => (
          <button
            key={item.id}
            className={`toolbar-btn ${activePanel === item.id ? "active" : ""}`}
            onClick={() => setActivePanel(item.id)}
            data-testid={`toolbar-${item.id}`}
          >
            {item.icon} {item.label}
          </button>
        ))}
        <div style={{ flex: 1 }}></div>
        <button
          className="toolbar-btn"
          onClick={() => setModal({ type: "changePassword" })}
          data-testid="change-password-btn"
        >
          🔑 Change Password
        </button>
        <button className="toolbar-btn" onClick={onLogout} data-testid="toolbar-logout-btn" style={{ background: "var(--red)", color: "var(--white)" }}>
          {Icons.logout} Logout
        </button>
      </div>

      {/* App Body */}
      <div className="app-body">
        {/* Left Navigation */}
        <div className="left-nav">
          <div className="nav-header">
            <h3>{getUserCampusName()}</h3>
            <p>Management System</p>
            {user.role === "superadmin" && (
              <select
                className="campus-select"
                value={selectedCampus}
                onChange={(e) => setSelectedCampus(e.target.value)}
                data-testid="campus-select"
              >
                {campuses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="nav-menu">
            <div className="nav-section">
              <div className="nav-section-title">Main Menu</div>
              {navItems.filter(item => ["gateway", "students", "attendance", "fees", "receipt"].includes(item.id)).map((item) => (
                <div
                  key={item.id}
                  className={`nav-item ${activePanel === item.id ? "active" : ""}`}
                  onClick={() => setActivePanel(item.id)}
                  data-testid={`nav-${item.id}`}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                  <span className="shortcut">{item.shortcut}</span>
                </div>
              ))}
            </div>
            {navItems.some(item => ["fee-structure", "fee-report", "attendance-report"].includes(item.id)) && (
              <div className="nav-section">
                <div className="nav-section-title">Reports & Setup</div>
                {navItems.filter(item => ["fee-structure", "fee-report", "attendance-report"].includes(item.id)).map((item) => (
                  <div
                    key={item.id}
                    className={`nav-item ${activePanel === item.id ? "active" : ""}`}
                    onClick={() => setActivePanel(item.id)}
                    data-testid={`nav-${item.id}`}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                    <span className="shortcut">{item.shortcut}</span>
                  </div>
                ))}
              </div>
            )}
            {navItems.some(item => ["notifications", "notif-history", "campuses", "settings"].includes(item.id)) && (
              <div className="nav-section">
                <div className="nav-section-title">Administration</div>
                {navItems.filter(item => ["notifications", "notif-history", "campuses", "settings"].includes(item.id)).map((item) => (
                  <div
                    key={item.id}
                    className={`nav-item ${activePanel === item.id ? "active" : ""}`}
                    onClick={() => setActivePanel(item.id)}
                    data-testid={`nav-${item.id}`}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                    <span className="shortcut">{item.shortcut}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="nav-footer">
            <div className="nav-user">
              <div className="nav-user-avatar">{user.name?.charAt(0) || "U"}</div>
              <div className="nav-user-info">
                <div className="name">{user.name}</div>
                <div className="role">{user.role}</div>
              </div>
            </div>
            <button className="logout-btn" onClick={onLogout} data-testid="logout-btn">
              {Icons.logout} Logout
            </button>
          </div>
        </div>

        {/* Right Content */}
        <div className="right-content">
          <div className="content-header">
            <h2>
              {navItems.find((n) => n.id === activePanel)?.icon}{" "}
              {navItems.find((n) => n.id === activePanel)?.label}
            </h2>
            <div className="shortcuts">
              Press F1-F9 for quick navigation | ESC to close
            </div>
          </div>

          <div className="content-scroll">
            {/* Gateway Panel */}
            <div className={`panel ${activePanel === "gateway" ? "active" : ""}`} data-testid="gateway-panel">
              <GatewayPanel stats={stats} onNavigate={setActivePanel} user={user} />
            </div>

            {/* Students Panel */}
            <div className={`panel ${activePanel === "students" ? "active" : ""}`} data-testid="students-panel">
              <StudentsPanel
                students={students}
                filters={filters}
                setFilters={setFilters}
                onRefresh={fetchStudents}
                onAdd={() => setModal({ 
                  type: "addStudent", 
                  prefill: { class_name: filters.class, section: filters.section } 
                })}
                onEdit={(student) => setModal({ type: "editStudent", student })}
                showToast={showToast}
                authHeaders={authHeaders}
              />
            </div>

            {/* Attendance Panel */}
            <div className={`panel ${activePanel === "attendance" ? "active" : ""}`} data-testid="attendance-panel">
              <AttendancePanel
                students={students}
                attendance={attendance}
                filters={filters}
                setFilters={setFilters}
                onRefresh={() => { fetchStudents(); fetchAttendance(); }}
                showToast={showToast}
                authHeaders={authHeaders}
              />
            </div>

            {/* Fees / Fee Deposit Panel */}
            <div className={`panel ${activePanel === "fees" ? "active" : ""}`} data-testid="fees-panel">
              <FeeDepositPanel
                showToast={showToast}
                authHeaders={authHeaders}
                user={user}
                campuses={campuses}
                settings={settings}
              />
            </div>

            {/* Fee Structure Panel */}
            <div className={`panel ${activePanel === "fee-structure" ? "active" : ""}`} data-testid="fee-structure-panel">
              <FeeStructurePanel showToast={showToast} authHeaders={authHeaders} />
            </div>

            {/* Fee Report Panel */}
            <div className={`panel ${activePanel === "fee-report" ? "active" : ""}`} data-testid="fee-report-panel">
              <FeeReportPanel showToast={showToast} authHeaders={authHeaders} />
            </div>

            {/* Attendance Report Panel */}
            <div className={`panel ${activePanel === "attendance-report" ? "active" : ""}`} data-testid="attendance-report-panel">
              <AttendanceReportPanel showToast={showToast} authHeaders={authHeaders} />
            </div>

            {/* Receipt Panel */}
            <div className={`panel ${activePanel === "receipt" ? "active" : ""}`} data-testid="receipt-panel">
              <ReceiptPanel receipts={receipts} settings={settings} user={user} campuses={campuses} />
            </div>

            {/* Notifications Panel */}
            <div className={`panel ${activePanel === "notifications" ? "active" : ""}`} data-testid="notifications-panel">
              <NotificationsPanel
                notifications={notifications}
                students={students}
                onRefresh={fetchNotifications}
                showToast={showToast}
                authHeaders={authHeaders}
              />
            </div>

            {/* Notification History Panel */}
            <div className={`panel ${activePanel === "notif-history" ? "active" : ""}`} data-testid="notif-history-panel">
              <NotificationHistoryPanel showToast={showToast} authHeaders={authHeaders} />
            </div>

            {/* Campuses Panel */}
            <div className={`panel ${activePanel === "campuses" ? "active" : ""}`} data-testid="campuses-panel">
              <CampusesPanel
                campuses={campuses}
                onRefresh={fetchCampuses}
                showToast={showToast}
                authHeaders={authHeaders}
                user={user}
              />
            </div>

            {/* Settings Panel */}
            <div className={`panel ${activePanel === "settings" ? "active" : ""}`} data-testid="settings-panel">
              <SettingsPanel
                settings={settings}
                onSave={fetchSettings}
                showToast={showToast}
                authHeaders={authHeaders}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-left">
          <span className="status-item">
            <span className="online">●</span> Connected
          </span>
          <span className="status-item">Students: {stats?.total_students || 0}</span>
          <span className="status-item">Today: ₹{stats?.today_collection?.toLocaleString() || 0}</span>
        </div>
        <div className="status-right">
          F1:Gateway F2:Students F3:Attendance F4:Fees F5:Receipt
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`} data-testid="toast">
          {toast.message}
        </div>
      )}

      {/* Modals */}
      {modal && (
        <ModalManager
          modal={modal}
          onClose={() => setModal(null)}
          students={students}
          fees={fees}
          showToast={showToast}
          authHeaders={authHeaders}
          onRefresh={() => {
            fetchStudents();
            fetchFees();
            fetchStats();
          }}
        />
      )}

      {loading && (
        <div className="modal-overlay">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== GATEWAY PANEL ====================
function GatewayPanel({ stats, onNavigate, user }) {
  const isTeacher = user?.role === "teacher";

  const allQuickLinks = [
    { id: "students", icon: Icons.students, label: "Student Master", shortcut: "F2" },
    { id: "attendance", icon: Icons.attendance, label: "Attendance Entry", shortcut: "F3" },
    { id: "fees", icon: Icons.fees, label: "Fee Collection", shortcut: "F4" },
    { id: "receipt", icon: Icons.receipt, label: "Receipt Print", shortcut: "F5" },
    { id: "notifications", icon: Icons.notifications, label: "Send Alerts", shortcut: "F6" },
    { id: "reports", icon: Icons.reports, label: "Reports", shortcut: "F7" }
  ];

  // Filter quick links for teacher - hide Fee Collection, Receipt Print, Reports
  const quickLinks = isTeacher 
    ? allQuickLinks.filter(l => !["fees", "receipt", "reports"].includes(l.id))
    : allQuickLinks;

  return (
    <>
      <div className="summary-row">
        <div className="summary-box">
          <div className="label">Total Students</div>
          <div className="value">{stats?.total_students || 0}</div>
        </div>
        <div className="summary-box">
          <div className="label">Present Today</div>
          <div className="value green">{stats?.present_today || 0}</div>
        </div>
        {!isTeacher && (
          <>
            <div className="summary-box">
              <div className="label">Total Fees</div>
              <div className="value">₹{stats?.total_fees?.toLocaleString() || 0}</div>
            </div>
            <div className="summary-box">
              <div className="label">Collected</div>
              <div className="value green">₹{stats?.total_collected?.toLocaleString() || 0}</div>
            </div>
            <div className="summary-box">
              <div className="label">Pending</div>
              <div className="value red">₹{stats?.total_pending?.toLocaleString() || 0}</div>
            </div>
            <div className="summary-box">
              <div className="label">Today Collection</div>
              <div className="value gold">₹{stats?.today_collection?.toLocaleString() || 0}</div>
            </div>
          </>
        )}
      </div>

      <div className="section-header">
        <h3>{Icons.gateway} Quick Access</h3>
      </div>

      <div className="gateway-groups">
        {quickLinks.map((link) => (
          <div
            key={link.id}
            className="gateway-item"
            onClick={() => onNavigate(link.id)}
            data-testid={`quick-${link.id}`}
          >
            <div className="icon">{link.icon}</div>
            <div className="text">{link.label}</div>
            <div className="shortcut">[{link.shortcut}]</div>
          </div>
        ))}
      </div>

      {!isTeacher && (
        <>
          <div className="section-header">
            <h3>📅 Today's Summary</h3>
          </div>

          <div className="daybook-summary">
            <div className="daybook-item">
              <div className="mode">Cash</div>
              <div className="amount">₹{((stats?.today_collection || 0) * 0.4).toLocaleString()}</div>
            </div>
            <div className="daybook-item">
              <div className="mode">UPI</div>
              <div className="amount">₹{((stats?.today_collection || 0) * 0.35).toLocaleString()}</div>
            </div>
            <div className="daybook-item">
              <div className="mode">Online</div>
              <div className="amount">₹{((stats?.today_collection || 0) * 0.25).toLocaleString()}</div>
            </div>
            <div className="daybook-item">
              <div className="mode">Receipts</div>
              <div className="amount">{stats?.today_receipts || 0}</div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ==================== STUDENTS PANEL ====================
function StudentsPanel({ students, filters, setFilters, onRefresh, onAdd, onEdit, showToast, authHeaders }) {
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const sections = ["A", "B", "C"];

  const deleteStudent = async (id) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await axios.delete(`${API}/students/${id}`, { headers: authHeaders });
      showToast("Student deleted successfully");
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Delete failed", "error");
    }
  };

  return (
    <>
      <div className="filters-row">
        <div className="filter-group">
          <label>Class:</label>
          <select
            className="filter-select"
            value={filters.class}
            onChange={(e) => setFilters({ ...filters, class: e.target.value })}
            data-testid="filter-class"
          >
            <option value="">All</option>
            {classes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Section:</label>
          <select
            className="filter-select"
            value={filters.section}
            onChange={(e) => setFilters({ ...filters, section: e.target.value })}
            data-testid="filter-section"
          >
            <option value="">All</option>
            {sections.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Name, Roll, Phone..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="filter-search"
          />
        </div>
        <button className="filter-btn" onClick={onRefresh} data-testid="search-students">
          {Icons.search} Search
        </button>
        <button className="filter-btn" onClick={onAdd} data-testid="add-student-panel">
          {Icons.add} Add Student
        </button>
      </div>

      <div className="tally-table">
        <div className="tally-table-header" style={{ gridTemplateColumns: "60px 180px 60px 60px 150px 120px 100px 100px" }}>
          <div>Roll</div>
          <div>Name</div>
          <div>Class</div>
          <div>Sec</div>
          <div>Father Name</div>
          <div>Phone</div>
          <div>Fee Status</div>
          <div>Actions</div>
        </div>
        <div className="tally-table-body">
          {students.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              No students found. Click "Seed Demo Data" to add sample data.
            </div>
          ) : (
            students.map((s) => (
              <div
                key={s.id}
                className="tally-table-row"
                style={{ gridTemplateColumns: "60px 180px 60px 60px 150px 120px 100px 100px" }}
                data-testid={`student-row-${s.id}`}
              >
                <div>{s.roll_no}</div>
                <div>{s.name}</div>
                <div>{s.class_name}</div>
                <div>{s.section}</div>
                <div>{s.father_name}</div>
                <div>{s.phone}</div>
                <div>
                  <span className={`badge ${s.fee_status}`}>{s.fee_status}</span>
                </div>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    className="att-btn"
                    onClick={() => onEdit(s)}
                    title="Edit"
                    data-testid={`edit-${s.id}`}
                    style={{ background: "var(--gold-light)" }}
                  >
                    {Icons.edit}
                  </button>
                  <button
                    className="att-btn"
                    onClick={() => deleteStudent(s.id)}
                    title="Delete"
                    data-testid={`delete-${s.id}`}
                  >
                    {Icons.delete}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="tally-table-footer">
          <span>Total: {students.length} students</span>
          <span>↑↓ Navigate | E Edit | Del Delete</span>
        </div>
      </div>
    </>
  );
}

// ==================== ATTENDANCE PANEL ====================
function AttendancePanel({ students, attendance, filters, setFilters, onRefresh, showToast, authHeaders }) {
  const [attendanceData, setAttendanceData] = useState({});
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const sections = ["A", "B"];

  useEffect(() => {
    const data = {};
    attendance.forEach((a) => {
      data[a.student_id] = a.status;
    });
    setAttendanceData(data);
  }, [attendance]);

  // Auto-load students and attendance when class/section/date changes
  useEffect(() => {
    if (filters.class && filters.section) {
      onRefresh();
    }
  }, [filters.class, filters.section, filters.date]);

  const filteredStudents = students.filter((s) => {
    if (filters.class && s.class_name !== filters.class) return false;
    if (filters.section && s.section !== filters.section) return false;
    return true;
  });

  const markAttendance = (studentId, status) => {
    setAttendanceData({ ...attendanceData, [studentId]: status });
  };

  const saveAttendance = async () => {
    if (!filters.class || !filters.section) {
      showToast("Please select class and section", "error");
      return;
    }

    const records = Object.entries(attendanceData).map(([student_id, status]) => ({
      student_id,
      status
    }));

    try {
      const res = await axios.post(
        `${API}/attendance/bulk`,
        {
          date: filters.date,
          class_name: filters.class,
          section: filters.section,
          attendance: records
        },
        { headers: authHeaders }
      );
      const n = res.data?.notifications || { sent: 0, skipped: 0, failed: 0 };
      const absentCount = res.data?.absent_leave_count || 0;
      let msg = "Attendance saved successfully!";
      if (absentCount > 0) {
        msg += ` | Notifications → Sent: ${n.sent}, Skipped: ${n.skipped}, Failed: ${n.failed}`;
        if (n.sent === 0 && n.skipped > 0) {
          msg += " (Configure MSG91 in Settings to send SMS/WhatsApp)";
        }
      }
      showToast(msg, n.failed > 0 ? "error" : "success");
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Save failed", "error");
    }
  };

  const markAllPresent = () => {
    const data = {};
    filteredStudents.forEach((s) => {
      data[s.id] = "P";
    });
    setAttendanceData(data);
  };

  return (
    <>
      <div className="filters-row sticky-filters" data-testid="attendance-filters">
        <div className="filter-group">
          <label>Date:</label>
          <input
            type="date"
            className="filter-input"
            value={filters.date}
            onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            data-testid="attendance-date"
          />
        </div>
        <div className="filter-group">
          <label>Class:</label>
          <select
            className="filter-select"
            value={filters.class}
            onChange={(e) => setFilters({ ...filters, class: e.target.value })}
            data-testid="attendance-class"
          >
            <option value="">Select</option>
            {classes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Section:</label>
          <select
            className="filter-select"
            value={filters.section}
            onChange={(e) => setFilters({ ...filters, section: e.target.value })}
            data-testid="attendance-section"
          >
            <option value="">Select</option>
            {sections.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <button className="filter-btn" onClick={markAllPresent} data-testid="mark-all-present">
          {Icons.attendance} All Present
        </button>
        <button className="filter-btn" onClick={saveAttendance} data-testid="save-attendance" style={{ background: "var(--green)", color: "#fff" }}>
          {Icons.add} Save Attendance
        </button>
      </div>

      <div className="attendance-grid">
        {filteredStudents.length === 0 ? (
          <div style={{ padding: "20px" }}>
            Select class and section to view students.
          </div>
        ) : (
          filteredStudents.map((s) => (
            <div key={s.id} className="attendance-item" data-testid={`att-${s.id}`}>
              <div className="roll">{s.roll_no}</div>
              <div className="name">{s.name}</div>
              <div className="status-btns">
                {["P", "A", "L"].map((status) => (
                  <button
                    key={status}
                    className={`att-btn ${status} ${attendanceData[s.id] === status ? "active" : ""}`}
                    onClick={() => markAttendance(s.id, status)}
                    data-testid={`att-${s.id}-${status}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ marginTop: "15px", padding: "10px", background: "var(--bg-light)" }}>
        <strong>Summary:</strong>{" "}
        Present: {Object.values(attendanceData).filter((v) => v === "P").length} |{" "}
        Absent: {Object.values(attendanceData).filter((v) => v === "A").length} |{" "}
        Leave: {Object.values(attendanceData).filter((v) => v === "L").length}
      </div>
    </>
  );
}

// ==================== FEES PANEL ====================
function FeesPanel({ fees, filters, setFilters, onRefresh, onPayment, showToast, authHeaders }) {
  const totalFees = fees.reduce((sum, f) => sum + f.amount, 0);
  const totalCollected = fees.reduce((sum, f) => sum + f.paid_amount, 0);
  const totalPending = fees.reduce((sum, f) => sum + f.balance, 0);

  return (
    <>
      <div className="summary-row">
        <div className="summary-box">
          <div className="label">Total Fees</div>
          <div className="value">₹{totalFees.toLocaleString()}</div>
        </div>
        <div className="summary-box">
          <div className="label">Collected</div>
          <div className="value green">₹{totalCollected.toLocaleString()}</div>
        </div>
        <div className="summary-box">
          <div className="label">Pending</div>
          <div className="value red">₹{totalPending.toLocaleString()}</div>
        </div>
        <div className="summary-box">
          <div className="label">Collection Rate</div>
          <div className="value gold">
            {totalFees > 0 ? ((totalCollected / totalFees) * 100).toFixed(1) : 0}%
          </div>
        </div>
      </div>

      <div className="filters-row">
        <div className="filter-group">
          <label>Status:</label>
          <select
            className="filter-select"
            value={filters.feeStatus}
            onChange={(e) => setFilters({ ...filters, feeStatus: e.target.value })}
            data-testid="fee-status-filter"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <button className="filter-btn" onClick={onRefresh} data-testid="refresh-fees">
          {Icons.refresh} Refresh
        </button>
      </div>

      <div className="tally-table">
        <div className="tally-table-header" style={{ gridTemplateColumns: "60px 180px 80px 80px 100px 100px 100px 80px 100px" }}>
          <div>Roll</div>
          <div>Student Name</div>
          <div>Class</div>
          <div>Type</div>
          <div>Amount</div>
          <div>Paid</div>
          <div>Balance</div>
          <div>Status</div>
          <div>Action</div>
        </div>
        <div className="tally-table-body">
          {fees.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              No fee records found.
            </div>
          ) : (
            fees.map((f) => (
              <div
                key={f.id}
                className="tally-table-row"
                style={{ gridTemplateColumns: "60px 180px 80px 80px 100px 100px 100px 80px 100px" }}
                data-testid={`fee-row-${f.id}`}
              >
                <div>{f.roll_no}</div>
                <div>{f.student_name}</div>
                <div>{f.class_name}-{f.section}</div>
                <div>{f.fee_type}</div>
                <div>₹{f.amount.toLocaleString()}</div>
                <div style={{ color: "var(--green)" }}>₹{f.paid_amount.toLocaleString()}</div>
                <div style={{ color: f.balance > 0 ? "var(--red)" : "var(--green)" }}>
                  ₹{f.balance.toLocaleString()}
                </div>
                <div>
                  <span className={`badge ${f.status}`}>{f.status}</span>
                </div>
                <div>
                  {f.status !== "paid" && (
                    <button
                      className="filter-btn"
                      onClick={() => onPayment(f)}
                      data-testid={`pay-${f.id}`}
                    >
                      Pay
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="tally-table-footer">
          <span>Total: {fees.length} records</span>
          <span>Enter to Pay | P Print Receipt</span>
        </div>
      </div>
    </>
  );
}

// ==================== RECEIPT PANEL ====================
function ReceiptPanel({ receipts, settings, user, campuses }) {
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Get user's campus info for the receipt header
  const getCampusInfo = () => {
    if (user?.campus_id && campuses.length > 0) {
      const userCampus = campuses.find(c => c.id === user.campus_id);
      if (userCampus) {
        return {
          name: userCampus.name,
          address: userCampus.address || settings.school_address || "",
          phone: userCampus.phone || settings.school_phone || "",
          principal: userCampus.principal_name || ""
        };
      }
    }
    return {
      name: settings.school_name || "VIDYALAYA Public School",
      address: settings.school_address || "",
      phone: settings.school_phone || "",
      principal: ""
    };
  };

  const campusInfo = getCampusInfo();

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
      <div>
        <div className="section-header">
          <h3>📄 Recent Receipts</h3>
        </div>
        <div className="tally-table">
          <div className="tally-table-header" style={{ gridTemplateColumns: "120px 150px 100px 80px" }}>
            <div>Receipt No</div>
            <div>Student</div>
            <div>Amount</div>
            <div>Mode</div>
          </div>
          <div className="tally-table-body">
            {receipts.map((r) => (
              <div
                key={r.id}
                className={`tally-table-row ${selectedReceipt?.id === r.id ? "selected" : ""}`}
                style={{ gridTemplateColumns: "120px 150px 100px 80px" }}
                onClick={() => setSelectedReceipt(r)}
                data-testid={`receipt-row-${r.id}`}
              >
                <div>{r.receipt_no}</div>
                <div>{r.student_name}</div>
                <div>₹{r.amount.toLocaleString()}</div>
                <div>{r.payment_mode}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="section-header">
          <h3>🖨 Receipt Preview</h3>
        </div>
        {selectedReceipt ? (
          <div className="receipt-preview" data-testid="receipt-preview">
            <div className="receipt-header">
              <h2>{campusInfo.name}</h2>
              {campusInfo.address && <p>{campusInfo.address}</p>}
              {campusInfo.phone && <p>Ph: {campusInfo.phone}</p>}
              {campusInfo.principal && <p>Principal: {campusInfo.principal}</p>}
            </div>
            <div className="receipt-title">FEE RECEIPT</div>
            <div className="receipt-info">
              <div className="receipt-info-item">
                <label>Receipt No:</label>
                <span>{selectedReceipt.receipt_no}</span>
              </div>
              <div className="receipt-info-item">
                <label>Date:</label>
                <span>{selectedReceipt.created_at?.split("T")[0]}</span>
              </div>
              <div className="receipt-info-item">
                <label>Student:</label>
                <span>{selectedReceipt.student_name}</span>
              </div>
              <div className="receipt-info-item">
                <label>Mode:</label>
                <span>{selectedReceipt.payment_mode?.toUpperCase()}</span>
              </div>
            </div>
            <table className="receipt-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Fee Payment</td>
                  <td>₹{selectedReceipt.amount.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
            <div className="receipt-total">
              Total: ₹{selectedReceipt.amount.toLocaleString()}
            </div>
            <div className="receipt-footer">
              <span>Received By: {selectedReceipt.received_by}</span>
              <span>Authorized Signature</span>
            </div>
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)" }}>
            Select a receipt to preview
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== NOTIFICATIONS PANEL ====================
function NotificationsPanel({ notifications, students, onRefresh, showToast, authHeaders }) {
  const [channel, setChannel] = useState("sms");
  const [recipientType, setRecipientType] = useState("all");
  const [message, setMessage] = useState("");
  const [selectedClass, setSelectedClass] = useState("");

  const templates = [
    { id: "fee_reminder", name: "Fee Reminder", text: "Dear Parent, Fee of ₹{amount} is pending for {student}. Please pay before {date}." },
    { id: "attendance", name: "Attendance Alert", text: "Dear Parent, {student} was marked absent today ({date})." },
    { id: "holiday", name: "Holiday Notice", text: "School will remain closed on {date} due to {reason}." },
    { id: "result", name: "Result Notification", text: "Dear Parent, Results for {exam} are declared. {student} secured {marks}." }
  ];

  const sendNotification = async () => {
    if (!message) {
      showToast("Please enter a message", "error");
      return;
    }

    try {
      await axios.post(
        `${API}/notifications/send`,
        {
          recipient_type: recipientType,
          class_name: selectedClass || undefined,
          channel: channel,
          template: "custom",
          message: message
        },
        { headers: authHeaders }
      );
      showToast("Notification sent successfully!");
      setMessage("");
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Send failed", "error");
    }
  };

  return (
    <div className="notification-composer">
      <div>
        <div className="section-header">
          <h3>✉ Compose Message</h3>
        </div>

        <div className="channel-options">
          {["sms", "whatsapp", "both"].map((c) => (
            <button
              key={c}
              className={`channel-btn ${channel === c ? "active" : ""}`}
              onClick={() => setChannel(c)}
              data-testid={`channel-${c}`}
            >
              {c === "sms" ? "📱 SMS" : c === "whatsapp" ? "💬 WhatsApp" : "📲 Both"}
            </button>
          ))}
        </div>

        <div className="filters-row" style={{ marginBottom: "10px" }}>
          <div className="filter-group">
            <label>Send to:</label>
            <select
              className="filter-select"
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
              data-testid="recipient-type"
            >
              <option value="all">All Students</option>
              <option value="class">By Class</option>
            </select>
          </div>
          {recipientType === "class" && (
            <div className="filter-group">
              <label>Class:</label>
              <select
                className="filter-select"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <option value="">Select</option>
                {["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="message-area">
          <textarea
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="notification-message"
          />
        </div>

        <div className="form-actions" style={{ justifyContent: "flex-start" }}>
          <button className="form-btn primary" onClick={sendNotification} data-testid="send-notification">
            {Icons.send} Send Notification
          </button>
        </div>

        <div style={{ marginTop: "15px", padding: "10px", background: "var(--bg-dark)", fontSize: "11px" }}>
          <strong>📢 How Notifications Work:</strong><br/>
          • Without MSG91 keys: Notifications are <strong>logged in the system</strong> for record keeping. You can view them in "Recent Notifications" below.<br/>
          • With MSG91 keys: Actual SMS/WhatsApp messages will be sent to parents' phones.<br/>
          • To enable live messaging: Go to <strong>Settings → MSG91 Configuration</strong> and add your API key.<br/>
          • <em>Current Status:</em> {notifications[0]?.msg91_integrated ? "✅ MSG91 Configured - Messages will be sent" : "📋 Logging Mode - Messages saved to records only"}
        </div>
      </div>

      <div>
        <div className="section-header">
          <h3>📋 Templates</h3>
        </div>
        <div className="template-list">
          {templates.map((t) => (
            <div
              key={t.id}
              className="template-item"
              onClick={() => setMessage(t.text)}
              data-testid={`template-${t.id}`}
            >
              <strong>{t.name}</strong>
              <p style={{ fontSize: "10px", color: "var(--text-light)", marginTop: "4px" }}>
                {t.text.substring(0, 50)}...
              </p>
            </div>
          ))}
        </div>

        <div className="section-header" style={{ marginTop: "15px" }}>
          <h3>📜 Recent Notifications</h3>
        </div>
        <div className="tally-table">
          <div className="tally-table-body" style={{ maxHeight: "200px" }}>
            {notifications.slice(0, 5).map((n) => (
              <div
                key={n.id}
                className="tally-table-row"
                style={{ gridTemplateColumns: "80px 60px 1fr" }}
              >
                <div>{n.created_at?.split("T")[0]}</div>
                <div>{n.channel}</div>
                <div>{n.message?.substring(0, 30)}...</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== REPORTS PANEL ====================
function ReportsPanel({ authHeaders }) {
  const [reportData, setReportData] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const reportCategories = [
    {
      title: "Student Reports",
      reports: [
        { id: "student_register", name: "Student Register", shortcut: "Alt+S" },
        { id: "class_strength", name: "Class Strength", shortcut: "Alt+C" }
      ]
    },
    {
      title: "Fee Reports",
      reports: [
        { id: "fee_collection", name: "Fee Collection Report", shortcut: "Alt+F" },
        { id: "defaulters", name: "Defaulters List", shortcut: "Alt+D" },
        { id: "daybook", name: "Day Book", shortcut: "Alt+B" }
      ]
    },
    {
      title: "Attendance Reports",
      reports: [
        { id: "attendance_monthly", name: "Monthly Attendance", shortcut: "Alt+A" },
        { id: "absentee_report", name: "Absentee Report", shortcut: "Alt+E" }
      ]
    }
  ];

  const loadReport = async (reportId) => {
    setSelectedReport(reportId);
    try {
      let res;
      switch (reportId) {
        case "student_register":
        case "class_strength":
          res = await axios.get(`${API}/reports/students`, { headers: authHeaders });
          break;
        case "fee_collection":
        case "defaulters":
          res = await axios.get(`${API}/reports/fees`, { headers: authHeaders });
          break;
        case "daybook":
          const today = new Date().toISOString().split("T")[0];
          res = await axios.get(`${API}/reports/daybook?date=${today}`, { headers: authHeaders });
          break;
        default:
          res = { data: {} };
      }
      setReportData(res.data);
    } catch (err) {
      console.error("Report error:", err);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "20px" }}>
      <div>
        <div className="reports-grid" style={{ gridTemplateColumns: "1fr" }}>
          {reportCategories.map((cat) => (
            <div key={cat.title} className="report-category">
              <div className="report-category-title">{cat.title}</div>
              <div className="report-list">
                {cat.reports.map((r) => (
                  <div
                    key={r.id}
                    className="report-item"
                    onClick={() => loadReport(r.id)}
                    data-testid={`report-${r.id}`}
                  >
                    <span>{r.name}</span>
                    <span className="shortcut">{r.shortcut}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="section-header">
          <h3>📊 Report Output</h3>
        </div>
        {reportData ? (
          <div className="form-panel">
            <div className="form-title">{selectedReport?.replace(/_/g, " ").toUpperCase()}</div>
            <pre style={{ 
              background: "var(--white)", 
              padding: "15px", 
              border: "var(--sunken)",
              overflow: "auto",
              maxHeight: "400px"
            }}>
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)" }}>
            Select a report from the left to view
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CAMPUSES PANEL ====================
function CampusesPanel({ campuses, onRefresh, showToast, authHeaders, user }) {
  const [newCampus, setNewCampus] = useState({ name: "", code: "", address: "", phone: "", principal_name: "" });
  const [newUser, setNewUser] = useState({ user_id: "", password: "", name: "", role: "admin", campus_id: "" });
  const [campusUsers, setCampusUsers] = useState([]);
  const [selectedCampusForUsers, setSelectedCampusForUsers] = useState("");
  const [campusActivity, setCampusActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // Fetch campus activity data
  const fetchCampusActivity = async () => {
    try {
      const res = await axios.get(`${API}/campuses/activity`, { headers: authHeaders });
      setCampusActivity(res.data);
      setShowActivity(true);
    } catch (err) {
      showToast("Failed to fetch activity data", "error");
    }
  };

  const addCampus = async () => {
    if (!newCampus.name || !newCampus.code) {
      showToast("Name and code are required", "error");
      return;
    }
    try {
      const res = await axios.post(`${API}/campuses`, newCampus, { headers: authHeaders });
      showToast("Campus added successfully!");
      setNewCampus({ name: "", code: "", address: "", phone: "", principal_name: "" });
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Add failed", "error");
    }
  };

  const deleteCampus = async (id) => {
    if (!window.confirm("Delete this campus?")) return;
    try {
      await axios.delete(`${API}/campuses/${id}`, { headers: authHeaders });
      showToast("Campus deleted");
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Delete failed", "error");
    }
  };

  const fetchCampusUsers = async (campusId) => {
    try {
      const res = await axios.get(`${API}/users?campus_id=${campusId}`, { headers: authHeaders });
      setCampusUsers(res.data);
    } catch (err) {
      setCampusUsers([]);
    }
  };

  const addUser = async () => {
    if (!newUser.user_id || !newUser.password || !newUser.name || !newUser.campus_id) {
      showToast("All fields are required", "error");
      return;
    }
    try {
      await axios.post(`${API}/auth/register`, newUser, { headers: authHeaders });
      showToast("User created successfully!");
      setNewUser({ user_id: "", password: "", name: "", role: "admin", campus_id: newUser.campus_id });
      fetchCampusUsers(newUser.campus_id);
    } catch (err) {
      showToast(err.response?.data?.detail || "Create user failed", "error");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: authHeaders });
      showToast("User deleted");
      fetchCampusUsers(selectedCampusForUsers);
    } catch (err) {
      showToast(err.response?.data?.detail || "Delete failed", "error");
    }
  };

  const saveCampusEdit = async () => {
    if (!editingCampus) return;
    try {
      const { id, name, code, address, phone, principal_name } = editingCampus;
      await axios.put(`${API}/campuses/${id}`, { name, code, address, phone, principal_name }, { headers: authHeaders });
      showToast("Campus updated");
      setEditingCampus(null);
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", "error");
    }
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    try {
      const payload = {
        name: editingUser.name,
        role: editingUser.role,
      };
      if (editingUser.password) payload.password = editingUser.password;
      await axios.put(`${API}/users/${editingUser.id}`, payload, { headers: authHeaders });
      showToast("User updated");
      setEditingUser(null);
      fetchCampusUsers(selectedCampusForUsers);
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", "error");
    }
  };

  return (
    <>
      {user.role === "superadmin" && (
        <div className="filters-row" style={{ marginBottom: "15px" }}>
          <button 
            className="filter-btn" 
            onClick={fetchCampusActivity}
            data-testid="view-activity-btn"
            style={{ background: showActivity ? "var(--gold)" : "var(--navy)", color: "var(--white)" }}
          >
            📊 {showActivity ? "Refresh" : "View"} School Usage Activity
          </button>
        </div>
      )}

      {showActivity && user.role === "superadmin" && (
        <div className="form-panel" style={{ marginBottom: "20px" }}>
          <div className="form-title">📊 School Usage Tracking</div>
          {campusActivity.length === 0 ? (
            <p style={{ padding: "10px", color: "var(--text-light)" }}>No campus activity recorded yet.</p>
          ) : (
            <div className="tally-table">
              <div className="tally-table-header" style={{ gridTemplateColumns: "150px 100px 150px 120px 100px 100px" }}>
                <div>School Name</div>
                <div>Code</div>
                <div>Last Login</div>
                <div>Last User</div>
                <div>Total Logins</div>
                <div>Status</div>
              </div>
              <div className="tally-table-body">
                {campusActivity.map((a) => (
                  <div
                    key={a.campus_id}
                    className="tally-table-row"
                    style={{ gridTemplateColumns: "150px 100px 150px 120px 100px 100px" }}
                  >
                    <div>{a.campus_name}</div>
                    <div>{a.campus_code}</div>
                    <div style={{ fontSize: "11px" }}>
                      {a.last_login ? new Date(a.last_login).toLocaleString('en-IN', { 
                        day: '2-digit', month: 'short', year: 'numeric', 
                        hour: '2-digit', minute: '2-digit' 
                      }) : "Never"}
                    </div>
                    <div style={{ fontSize: "11px" }}>
                      {a.last_user || "-"}
                      {a.last_user_role && <span style={{ color: "var(--text-light)" }}> ({a.last_user_role})</span>}
                    </div>
                    <div style={{ fontWeight: "bold" }}>{a.login_count}</div>
                    <div>
                      <span 
                        className={`badge ${
                          a.status === "Active Today" ? "present" : 
                          a.status.includes("Active") ? "paid" : 
                          a.status === "Never" ? "pending" : "absent"
                        }`}
                        style={{ fontSize: "10px" }}
                      >
                        {a.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="tally-table-footer">
                <span>Total Schools: {campusActivity.length}</span>
                <span>
                  Active Today: {campusActivity.filter(a => a.status === "Active Today").length} | 
                  Never Used: {campusActivity.filter(a => a.status === "Never").length}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="section-header">
        <h3>🏫 Campuses List</h3>
      </div>
      <div className="tally-table">
        <div className="tally-table-inner">
          <div className="tally-table-header" style={{ gridTemplateColumns: "80px 200px 200px 120px 120px 140px" }}>
            <div>Code</div>
            <div>Name</div>
            <div>Address</div>
            <div>Phone</div>
            <div>Principal</div>
            <div>Action</div>
          </div>
          <div className="tally-table-body">
            {campuses.map((c) => (
              <div
                key={c.id}
                className="tally-table-row"
                style={{ gridTemplateColumns: "80px 200px 200px 120px 120px 140px" }}
                data-testid={`campus-row-${c.id}`}
              >
                <div>{c.code}</div>
                <div>{c.name}</div>
                <div>{c.address || "-"}</div>
                <div>{c.phone || "-"}</div>
                <div>{c.principal_name || "-"}</div>
                <div>
                  {!c.is_default && user.role === "superadmin" && (
                    <>
                      <button
                        className="att-btn"
                        onClick={() => setEditingCampus({ ...c })}
                        title="Edit Campus"
                        data-testid={`edit-campus-${c.id}`}
                        style={{ marginRight: "4px", background: "var(--gold)" }}
                      >
                        ✏️
                      </button>
                      <button className="att-btn" onClick={() => deleteCampus(c.id)} title="Delete Campus" data-testid={`delete-campus-${c.id}`}>
                        {Icons.delete}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editingCampus && (
        <div className="modal-overlay" onClick={() => setEditingCampus(null)}>
          <div className="modal-window" onClick={(e) => e.stopPropagation()} style={{ width: "500px" }}>
            <div className="modal-title-bar">
              <span>✏️ Edit Campus - {editingCampus.name}</span>
              <button className="modal-close" onClick={() => setEditingCampus(null)}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-grid">
                <div className="form-row">
                  <label>Name:</label>
                  <input type="text" value={editingCampus.name || ""} onChange={(e) => setEditingCampus({ ...editingCampus, name: e.target.value })} data-testid="edit-campus-name" />
                </div>
                <div className="form-row">
                  <label>Code:</label>
                  <input type="text" value={editingCampus.code || ""} onChange={(e) => setEditingCampus({ ...editingCampus, code: e.target.value.toUpperCase() })} data-testid="edit-campus-code" />
                </div>
                <div className="form-row">
                  <label>Address:</label>
                  <input type="text" value={editingCampus.address || ""} onChange={(e) => setEditingCampus({ ...editingCampus, address: e.target.value })} data-testid="edit-campus-address" />
                </div>
                <div className="form-row">
                  <label>Phone:</label>
                  <input type="text" value={editingCampus.phone || ""} onChange={(e) => setEditingCampus({ ...editingCampus, phone: e.target.value })} data-testid="edit-campus-phone" />
                </div>
                <div className="form-row">
                  <label>Principal:</label>
                  <input type="text" value={editingCampus.principal_name || ""} onChange={(e) => setEditingCampus({ ...editingCampus, principal_name: e.target.value })} data-testid="edit-campus-principal" />
                </div>
              </div>
              <div className="form-actions">
                <button className="form-btn secondary" onClick={() => setEditingCampus(null)}>Cancel</button>
                <button className="form-btn primary" onClick={saveCampusEdit} data-testid="save-campus-edit">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {user.role === "superadmin" && (
        <>
          <div className="form-panel" style={{ marginTop: "20px" }}>
            <div className="form-title">Add New Campus</div>
            <div className="form-grid">
              <div className="form-row">
                <label>Name:</label>
                <input
                  type="text"
                  value={newCampus.name}
                  onChange={(e) => setNewCampus({ ...newCampus, name: e.target.value })}
                  data-testid="campus-name"
                  placeholder="e.g., Rashtriya Public School"
                />
              </div>
              <div className="form-row">
                <label>Code:</label>
                <input
                  type="text"
                  value={newCampus.code}
                  onChange={(e) => setNewCampus({ ...newCampus, code: e.target.value.toUpperCase() })}
                  data-testid="campus-code"
                  placeholder="e.g., RPS"
                />
              </div>
              <div className="form-row">
                <label>Address:</label>
                <input
                  type="text"
                  value={newCampus.address}
                  onChange={(e) => setNewCampus({ ...newCampus, address: e.target.value })}
                  placeholder="Full address"
                />
              </div>
              <div className="form-row">
                <label>Phone:</label>
                <input
                  type="text"
                  value={newCampus.phone}
                  onChange={(e) => setNewCampus({ ...newCampus, phone: e.target.value })}
                  placeholder="Contact number"
                />
              </div>
              <div className="form-row">
                <label>Principal:</label>
                <input
                  type="text"
                  value={newCampus.principal_name}
                  onChange={(e) => setNewCampus({ ...newCampus, principal_name: e.target.value })}
                  placeholder="Principal name"
                />
              </div>
            </div>
            <div className="form-actions">
              <button className="form-btn primary" onClick={addCampus} data-testid="add-campus">
                {Icons.add} Add Campus
              </button>
            </div>
          </div>

          <div className="form-panel" style={{ marginTop: "20px" }}>
            <div className="form-title">👤 Manage Campus Users (Admin/Accountant/Teacher)</div>
            
            <div className="form-row" style={{ marginBottom: "15px" }}>
              <label>Select Campus:</label>
              <select
                value={selectedCampusForUsers}
                onChange={(e) => {
                  setSelectedCampusForUsers(e.target.value);
                  setNewUser({ ...newUser, campus_id: e.target.value });
                  if (e.target.value) fetchCampusUsers(e.target.value);
                }}
                style={{ flex: 1 }}
              >
                <option value="">-- Select Campus --</option>
                {campuses.filter(c => !c.is_default).map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </div>

            {selectedCampusForUsers && (
              <>
                <div className="section-header" style={{ marginTop: "10px" }}>
                  <h3>Existing Users</h3>
                </div>
                {campusUsers.length > 0 ? (
                  <div className="tally-table" style={{ marginBottom: "15px" }}>
                    <div className="tally-table-inner">
                      <div className="tally-table-header" style={{ gridTemplateColumns: "120px 150px 100px 140px" }}>
                        <div>User ID</div>
                        <div>Name</div>
                        <div>Role</div>
                        <div>Action</div>
                      </div>
                      <div className="tally-table-body">
                        {campusUsers.map((u) => (
                          <div key={u.id} className="tally-table-row" style={{ gridTemplateColumns: "120px 150px 100px 140px" }}>
                            <div>{u.user_id}</div>
                            <div>{u.name}</div>
                            <div><span className="badge paid">{u.role}</span></div>
                            <div>
                              <button
                                className="att-btn"
                                onClick={() => setEditingUser({ ...u, password: "" })}
                                title="Edit User"
                                data-testid={`edit-user-${u.id}`}
                                style={{ marginRight: "4px", background: "var(--gold)" }}
                              >
                                ✏️
                              </button>
                              <button className="att-btn" onClick={() => deleteUser(u.id)} title="Delete User" data-testid={`delete-user-${u.id}`}>
                                {Icons.delete}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p style={{ padding: "10px", background: "var(--bg-dark)", marginBottom: "15px" }}>
                    No users created for this campus yet.
                  </p>
                )}

                <div className="section-header">
                  <h3>Create New User</h3>
                </div>
                <div className="form-grid">
                  <div className="form-row">
                    <label>User ID:</label>
                    <input
                      type="text"
                      value={newUser.user_id}
                      onChange={(e) => setNewUser({ ...newUser, user_id: e.target.value })}
                      placeholder="Login ID (e.g., rps_admin)"
                    />
                  </div>
                  <div className="form-row">
                    <label>Password:</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Password"
                    />
                  </div>
                  <div className="form-row">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="form-row">
                    <label>Role:</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    >
                      <option value="admin">Admin</option>
                      <option value="accountant">Accountant</option>
                      <option value="teacher">Teacher</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button className="form-btn primary" onClick={addUser}>
                    {Icons.add} Create User
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal-window" onClick={(e) => e.stopPropagation()} style={{ width: "500px" }}>
            <div className="modal-title-bar">
              <span>✏️ Edit User - {editingUser.user_id}</span>
              <button className="modal-close" onClick={() => setEditingUser(null)}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-grid">
                <div className="form-row">
                  <label>User ID:</label>
                  <input type="text" value={editingUser.user_id || ""} disabled style={{ background: "var(--bg-dark)" }} />
                </div>
                <div className="form-row">
                  <label>Name:</label>
                  <input type="text" value={editingUser.name || ""} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} data-testid="edit-user-name" />
                </div>
                <div className="form-row">
                  <label>Role:</label>
                  <select value={editingUser.role || "admin"} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })} data-testid="edit-user-role">
                    <option value="admin">Admin</option>
                    <option value="accountant">Accountant</option>
                    <option value="teacher">Teacher</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>New Password:</label>
                  <input type="password" value={editingUser.password || ""} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })} placeholder="Leave blank to keep existing" data-testid="edit-user-password" />
                </div>
              </div>
              <div className="form-actions">
                <button className="form-btn secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                <button className="form-btn primary" onClick={saveUserEdit} data-testid="save-user-edit">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
function SettingsPanel({ settings, onSave, showToast, authHeaders }) {
  const [formData, setFormData] = useState({
    school_name: "",
    school_address: "",
    school_phone: "",
    school_email: "",
    msg_enabled: false,
    msg91_authkey: "",
    whatsapp_enabled: false,
    whatsapp_integrated_number: "",
    sms_enabled: false,
    msg91_sender_id: "",
    sms_fallback_enabled: true,
    default_country_code: "91",
    template_absent: "",
    template_leave: "",
    template_fee_payment: "",
    template_fee_due: "",
    sms_flow_absent: "",
    sms_flow_leave: "",
    sms_flow_fee_payment: "",
    sms_flow_fee_due: "",
    razorpay_key_id: "",
    razorpay_key_secret: ""
  });

  useEffect(() => {
    setFormData({
      school_name: settings.school_name || "",
      school_address: settings.school_address || "",
      school_phone: settings.school_phone || "",
      school_email: settings.school_email || "",
      msg_enabled: !!settings.msg_enabled,
      msg91_authkey: "",  // Never populate from API - keep as blank to preserve secret
      whatsapp_enabled: !!settings.whatsapp_enabled,
      whatsapp_integrated_number: settings.whatsapp_integrated_number || "",
      sms_enabled: !!settings.sms_enabled,
      msg91_sender_id: settings.msg91_sender_id || "",
      sms_fallback_enabled: settings.sms_fallback_enabled !== false,
      default_country_code: settings.default_country_code || "91",
      template_absent: settings.template_absent || "",
      template_leave: settings.template_leave || "",
      template_fee_payment: settings.template_fee_payment || "",
      template_fee_due: settings.template_fee_due || "",
      sms_flow_absent: settings.sms_flow_absent || "",
      sms_flow_leave: settings.sms_flow_leave || "",
      sms_flow_fee_payment: settings.sms_flow_fee_payment || "",
      sms_flow_fee_due: settings.sms_flow_fee_due || "",
      razorpay_key_id: "",
      razorpay_key_secret: ""
    });
  }, [settings]);

  const saveSettings = async () => {
    try {
      const data = {};
      Object.entries(formData).forEach(([key, value]) => {
        // Include booleans always; include strings only if non-empty (avoid overwriting secrets)
        if (typeof value === "boolean") data[key] = value;
        else if (value !== "" && value !== null && value !== undefined) data[key] = value;
      });
      await axios.put(`${API}/settings`, data, { headers: authHeaders });
      showToast("Settings saved successfully!");
      onSave();
    } catch (err) {
      showToast(err.response?.data?.detail || "Save failed", "error");
    }
  };

  return (
    <>
      <div className="settings-section">
        <div className="settings-section-title">School Information</div>
        <div className="form-panel">
          <div className="form-grid">
            <div className="form-row">
              <label>School Name:</label>
              <input
                type="text"
                value={formData.school_name}
                onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                data-testid="setting-school-name"
              />
            </div>
            <div className="form-row">
              <label>Address:</label>
              <input
                type="text"
                value={formData.school_address}
                onChange={(e) => setFormData({ ...formData, school_address: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>Phone:</label>
              <input
                type="text"
                value={formData.school_phone}
                onChange={(e) => setFormData({ ...formData, school_phone: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>Email:</label>
              <input
                type="email"
                value={formData.school_email}
                onChange={(e) => setFormData({ ...formData, school_email: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">📱 Communication (MSG91 - SMS & WhatsApp)</div>
        <div className="form-panel">
          <div className="form-grid">
            <div className="form-row">
              <label>Enable Messaging:</label>
              <select
                value={formData.msg_enabled ? "on" : "off"}
                onChange={(e) => setFormData({ ...formData, msg_enabled: e.target.value === "on" })}
                data-testid="setting-msg-enabled"
              >
                <option value="off">OFF</option>
                <option value="on">ON</option>
              </select>
            </div>
            <div className="form-row">
              <label>Default Country Code:</label>
              <input
                type="text"
                placeholder="91"
                value={formData.default_country_code}
                onChange={(e) => setFormData({ ...formData, default_country_code: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>MSG91 Auth Key:</label>
              <input
                type="password"
                placeholder={settings.msg91_configured ? settings.msg91_authkey_masked || "••••••••" : "Enter MSG91 Auth Key"}
                value={formData.msg91_authkey}
                onChange={(e) => setFormData({ ...formData, msg91_authkey: e.target.value })}
                data-testid="setting-msg91-key"
              />
            </div>
            <div className="form-row">
              <label>WhatsApp Enabled:</label>
              <select
                value={formData.whatsapp_enabled ? "on" : "off"}
                onChange={(e) => setFormData({ ...formData, whatsapp_enabled: e.target.value === "on" })}
                data-testid="setting-wa-enabled"
              >
                <option value="off">OFF</option>
                <option value="on">ON</option>
              </select>
            </div>
            <div className="form-row">
              <label>WhatsApp Integrated Number:</label>
              <input
                type="text"
                placeholder="e.g., 919876543210"
                value={formData.whatsapp_integrated_number}
                onChange={(e) => setFormData({ ...formData, whatsapp_integrated_number: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>SMS Enabled:</label>
              <select
                value={formData.sms_enabled ? "on" : "off"}
                onChange={(e) => setFormData({ ...formData, sms_enabled: e.target.value === "on" })}
                data-testid="setting-sms-enabled"
              >
                <option value="off">OFF</option>
                <option value="on">ON</option>
              </select>
            </div>
            <div className="form-row">
              <label>SMS Sender ID:</label>
              <input
                type="text"
                placeholder="e.g., VIDYAL"
                value={formData.msg91_sender_id}
                onChange={(e) => setFormData({ ...formData, msg91_sender_id: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>Enable SMS Fallback:</label>
              <select
                value={formData.sms_fallback_enabled ? "on" : "off"}
                onChange={(e) => setFormData({ ...formData, sms_fallback_enabled: e.target.value === "on" })}
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            </div>
          </div>

          <div className="settings-section-title" style={{ fontSize: "12px", marginTop: "15px" }}>WhatsApp Template Names</div>
          <div className="form-grid">
            <div className="form-row">
              <label>Absent Template:</label>
              <input type="text" placeholder="student_absent" value={formData.template_absent} onChange={(e) => setFormData({ ...formData, template_absent: e.target.value })} />
            </div>
            <div className="form-row">
              <label>Leave Template:</label>
              <input type="text" placeholder="student_leave" value={formData.template_leave} onChange={(e) => setFormData({ ...formData, template_leave: e.target.value })} />
            </div>
            <div className="form-row">
              <label>Fee Payment Template:</label>
              <input type="text" placeholder="fee_payment_received" value={formData.template_fee_payment} onChange={(e) => setFormData({ ...formData, template_fee_payment: e.target.value })} />
            </div>
            <div className="form-row">
              <label>Fee Due Template:</label>
              <input type="text" placeholder="fee_due" value={formData.template_fee_due} onChange={(e) => setFormData({ ...formData, template_fee_due: e.target.value })} />
            </div>
          </div>

          <div className="settings-section-title" style={{ fontSize: "12px", marginTop: "15px" }}>SMS DLT Flow IDs</div>
          <div className="form-grid">
            <div className="form-row">
              <label>SMS Flow - Absent:</label>
              <input type="text" placeholder="MSG91 flow_id" value={formData.sms_flow_absent} onChange={(e) => setFormData({ ...formData, sms_flow_absent: e.target.value })} />
            </div>
            <div className="form-row">
              <label>SMS Flow - Leave:</label>
              <input type="text" placeholder="MSG91 flow_id" value={formData.sms_flow_leave} onChange={(e) => setFormData({ ...formData, sms_flow_leave: e.target.value })} />
            </div>
            <div className="form-row">
              <label>SMS Flow - Fee Payment:</label>
              <input type="text" placeholder="MSG91 flow_id" value={formData.sms_flow_fee_payment} onChange={(e) => setFormData({ ...formData, sms_flow_fee_payment: e.target.value })} />
            </div>
            <div className="form-row">
              <label>SMS Flow - Fee Due:</label>
              <input type="text" placeholder="MSG91 flow_id" value={formData.sms_flow_fee_due} onChange={(e) => setFormData({ ...formData, sms_flow_fee_due: e.target.value })} />
            </div>
          </div>

          <TestMessageForm authHeaders={authHeaders} showToast={showToast} />

          <p style={{ fontSize: "11px", color: "var(--text-light)", marginTop: "10px" }}>
            Get MSG91 credentials from <a href="https://msg91.com" target="_blank" rel="noreferrer">msg91.com</a>.
            Auth Key is stored securely and never exposed in the frontend.
          </p>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Razorpay Configuration (Online Payments)</div>
        <div className="form-panel">
          <div className="form-grid">
            <div className="form-row">
              <label>Key ID:</label>
              <input
                type="text"
                placeholder={settings.razorpay_configured ? "••••••••" : "rzp_test_xxxxx"}
                value={formData.razorpay_key_id}
                onChange={(e) => setFormData({ ...formData, razorpay_key_id: e.target.value })}
                data-testid="setting-razorpay-key"
              />
            </div>
            <div className="form-row">
              <label>Key Secret:</label>
              <input
                type="password"
                placeholder={settings.razorpay_configured ? "••••••••" : "Enter Key Secret"}
                value={formData.razorpay_key_secret}
                onChange={(e) => setFormData({ ...formData, razorpay_key_secret: e.target.value })}
              />
            </div>
          </div>
          <p style={{ fontSize: "11px", color: "var(--text-light)", marginTop: "10px" }}>
            Get your Razorpay credentials from <a href="https://razorpay.com" target="_blank" rel="noreferrer">razorpay.com</a>
          </p>
        </div>
      </div>

      <div className="form-actions" style={{ justifyContent: "flex-start" }}>
        <button className="form-btn primary" onClick={saveSettings} data-testid="save-settings">
          {Icons.add} Save Settings
        </button>
      </div>
    </>
  );
}

// ==================== FEE STRUCTURE PANEL ====================
function FeeStructurePanel({ showToast, authHeaders }) {
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const [selectedClass, setSelectedClass] = useState("");
  const [feeItems, setFeeItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const feeTypeOptions = ["Tuition Fee", "Transport Fee", "Exam Fee", "Admission Fee", "Book Fee", "Uniform Fee", "Sports Fee", "Computer Fee", "Library Fee", "Development Fee", "Other"];

  const loadStructure = async (className) => {
    if (!className) {
      setFeeItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fee-structures?class_name=${className}`, { headers: authHeaders });
      if (res.data.length > 0) {
        setFeeItems(res.data[0].fee_items);
      } else {
        setFeeItems([]);
      }
    } catch (err) {
      setFeeItems([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedClass) loadStructure(selectedClass);
  }, [selectedClass]);

  const addFeeItem = () => {
    setFeeItems([...feeItems, { fee_type: "Tuition Fee", amount: 0, frequency: "monthly" }]);
  };

  const updateItem = (idx, field, value) => {
    const updated = [...feeItems];
    updated[idx][field] = field === "amount" ? parseFloat(value) || 0 : value;
    setFeeItems(updated);
  };

  const removeItem = (idx) => {
    setFeeItems(feeItems.filter((_, i) => i !== idx));
  };

  const saveStructure = async () => {
    if (!selectedClass) {
      showToast("Please select a class", "error");
      return;
    }
    if (feeItems.length === 0) {
      showToast("Please add at least one fee item", "error");
      return;
    }
    try {
      await axios.post(`${API}/fee-structures`, {
        class_name: selectedClass,
        academic_year: "2026-27",
        fee_items: feeItems
      }, { headers: authHeaders });
      showToast("Fee structure saved successfully!");
    } catch (err) {
      showToast(err.response?.data?.detail || "Save failed", "error");
    }
  };

  const totalMonthly = feeItems.filter(i => i.frequency === "monthly").reduce((s, i) => s + (i.amount || 0), 0);
  const totalYearly = feeItems.filter(i => i.frequency === "yearly" || i.frequency === "one_time").reduce((s, i) => s + (i.amount || 0), 0);
  const totalAnnual = totalMonthly * 12 + totalYearly;

  return (
    <>
      <div className="filters-row">
        <div className="filter-group">
          <label>Class:</label>
          <select
            className="filter-select"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            data-testid="fee-structure-class"
          >
            <option value="">Select Class</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {selectedClass && (
          <>
            <button className="filter-btn" onClick={addFeeItem} data-testid="add-fee-item">
              + Add Fee Item
            </button>
            <button className="filter-btn" onClick={saveStructure} data-testid="save-fee-structure" style={{ background: "var(--green)", color: "#fff" }}>
              Save Structure
            </button>
          </>
        )}
      </div>

      {selectedClass ? (
        <>
          <div className="section-header">
            <h3>Fee Structure for Class {selectedClass}</h3>
          </div>
          <div className="tally-table">
            <div className="tally-table-header" style={{ gridTemplateColumns: "200px 150px 150px 80px" }}>
              <div>Fee Type</div>
              <div>Amount (₹)</div>
              <div>Frequency</div>
              <div>Action</div>
            </div>
            <div className="tally-table-body">
              {feeItems.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  No fee items. Click "Add Fee Item" to start.
                </div>
              ) : (
                feeItems.map((item, idx) => (
                  <div key={idx} className="tally-table-row" style={{ gridTemplateColumns: "200px 150px 150px 80px" }}>
                    <div>
                      <select value={item.fee_type} onChange={(e) => updateItem(idx, "fee_type", e.target.value)} style={{ width: "100%" }}>
                        {feeTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <input type="number" value={item.amount} onChange={(e) => updateItem(idx, "amount", e.target.value)} style={{ width: "100%" }} data-testid={`fee-amount-${idx}`} />
                    </div>
                    <div>
                      <select value={item.frequency} onChange={(e) => updateItem(idx, "frequency", e.target.value)} style={{ width: "100%" }}>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="one_time">One-time</option>
                      </select>
                    </div>
                    <div>
                      <button className="att-btn" onClick={() => removeItem(idx)}>×</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="summary-row" style={{ marginTop: "15px" }}>
            <div className="summary-box">
              <div className="label">Monthly Total</div>
              <div className="value">₹{totalMonthly.toLocaleString()}</div>
            </div>
            <div className="summary-box">
              <div className="label">Yearly/One-time</div>
              <div className="value">₹{totalYearly.toLocaleString()}</div>
            </div>
            <div className="summary-box">
              <div className="label">Annual Total</div>
              <div className="value green">₹{totalAnnual.toLocaleString()}</div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)" }}>
          Select a class to configure fee structure
        </div>
      )}
    </>
  );
}

// ==================== FEE DEPOSIT PANEL ====================
function FeeDepositPanel({ showToast, authHeaders, user, campuses, settings }) {
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const sections = ["A", "B", "C"];
  const months = [
    { v: "2026-04", n: "Apr 2026" }, { v: "2026-05", n: "May 2026" }, { v: "2026-06", n: "Jun 2026" },
    { v: "2026-07", n: "Jul 2026" }, { v: "2026-08", n: "Aug 2026" }, { v: "2026-09", n: "Sep 2026" },
    { v: "2026-10", n: "Oct 2026" }, { v: "2026-11", n: "Nov 2026" }, { v: "2026-12", n: "Dec 2026" },
    { v: "2027-01", n: "Jan 2027" }, { v: "2027-02", n: "Feb 2027" }, { v: "2027-03", n: "Mar 2027" }
  ];

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([new Date().toISOString().slice(0, 7)]);
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [feeStructure, setFeeStructure] = useState(null);
  const [feeItems, setFeeItems] = useState([]);
  const [paidAmount, setPaidAmount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [transactionId, setTransactionId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [previousDues, setPreviousDues] = useState(0);

  const loadStudents = async () => {
    if (!selectedClass || !selectedSection) {
      setStudents([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/students?class_name=${selectedClass}&section=${selectedSection}`, { headers: authHeaders });
      setStudents(res.data);
    } catch (err) {
      setStudents([]);
    }
  };

  useEffect(() => { loadStudents(); }, [selectedClass, selectedSection]);

  const loadStudentFeeStructure = async (studentId) => {
    try {
      const res = await axios.get(`${API}/students/${studentId}/fee-structure`, { headers: authHeaders });
      setFeeStructure(res.data);
      
      // Calculate current dues based on selected months
      const items = res.data.fee_items.map(item => ({
        fee_type: item.fee_type,
        amount: (item.frequency === "monthly" ? item.amount * selectedMonths.length : item.amount),
        frequency: item.frequency,
        original_amount: item.amount
      }));
      setFeeItems(items);
      // Auto-fill paid amount to full total (editable)
      const total = items.reduce((s, i) => s + (i.amount || 0), 0);
      setPaidAmount(total);
      
      // Calculate previous months' dues (not yet paid)
      // For now, simple calculation - complete previous dues logic would be more complex
      setPreviousDues(0);
    } catch (err) {
      showToast("Failed to load fee structure", "error");
    }
  };

  const selectStudent = (student) => {
    setSelectedStudent(student);
    loadStudentFeeStructure(student.id);
    setDiscount(0);
    setMarkAsPaid(false);
  };

  const toggleMonth = (monthValue) => {
    if (selectedMonths.includes(monthValue)) {
      setSelectedMonths(selectedMonths.filter(m => m !== monthValue));
    } else {
      setSelectedMonths([...selectedMonths, monthValue]);
    }
  };

  useEffect(() => {
    // Recalculate fee items when months change
    if (feeStructure) {
      const items = feeStructure.fee_items.map(item => ({
        fee_type: item.fee_type,
        amount: (item.frequency === "monthly" ? (item.original_amount || item.amount) * selectedMonths.length : (item.original_amount || item.amount)),
        frequency: item.frequency,
        original_amount: item.original_amount || item.amount
      }));
      setFeeItems(items);
      // Auto-fill paid amount to total (editable/reducible by user)
      const total = items.reduce((s, i) => s + (i.amount || 0), 0);
      setPaidAmount(total);
    }
  }, [selectedMonths]);

  const updateFeeAmount = (idx, amount) => {
    const updated = [...feeItems];
    updated[idx].amount = parseFloat(amount) || 0;
    setFeeItems(updated);
  };

  const totalAmount = feeItems.reduce((s, i) => s + (i.amount || 0), 0) + previousDues;

  const submitFee = async () => {
    if (!selectedStudent) {
      showToast("Please select a student", "error");
      return;
    }
    if (selectedMonths.length === 0) {
      showToast("Please select at least one month", "error");
      return;
    }
    if (paidAmount <= 0 && !markAsPaid) {
      showToast("Please enter paid amount", "error");
      return;
    }

    try {
      const res = await axios.post(`${API}/fee-deposits`, {
        student_id: selectedStudent.id,
        month: selectedMonths.sort().join(","),
        academic_year: "2026-27",
        fee_items: feeItems,
        total_amount: totalAmount,
        paid_amount: paidAmount,
        discount: discount,
        payment_mode: paymentMode,
        transaction_id: transactionId,
        remarks: remarks,
        mark_as_paid: markAsPaid
      }, { headers: authHeaders });

      showToast(`Fee deposited! Receipt: ${res.data.receipt_no}`);
      
      // Auto-print receipt
      setTimeout(() => printReceipt(res.data.receipt_no), 500);
      
      // Reset form
      setSelectedStudent(null);
      setFeeItems([]);
      setPaidAmount(0);
      setDiscount(0);
      setRemarks("");
      setTransactionId("");
      setMarkAsPaid(false);
    } catch (err) {
      showToast(err.response?.data?.detail || "Deposit failed", "error");
    }
  };

  const printReceipt = async (receiptNo) => {
    try {
      const res = await axios.get(`${API}/fee-deposits/receipt/${receiptNo}`, { headers: authHeaders });
      const receipt = res.data;
      
      // Get campus info
      let campusInfo = {
        name: settings.school_name || "VIDYALAYA Public School",
        address: settings.school_address || "",
        phone: settings.school_phone || ""
      };
      if (user?.campus_id && campuses.length > 0) {
        const userCampus = campuses.find(c => c.id === user.campus_id);
        if (userCampus) {
          campusInfo = {
            name: userCampus.name,
            address: userCampus.address || "",
            phone: userCampus.phone || ""
          };
        }
      }
      
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
        <head>
          <title>Receipt ${receiptNo}</title>
          <style>
            body { font-family: 'Courier New', monospace; padding: 20px; max-width: 600px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #001f4d; padding-bottom: 10px; margin-bottom: 15px; }
            .header h2 { color: #001f4d; margin: 0 0 5px 0; }
            .title { background: #001f4d; color: white; padding: 8px; text-align: center; font-weight: bold; margin-bottom: 15px; }
            .info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #888; padding: 6px 8px; text-align: left; font-size: 12px; }
            th { background: #d4cfc4; }
            .total { text-align: right; font-size: 16px; font-weight: bold; color: #001f4d; padding: 8px; border-top: 2px solid #001f4d; }
            .footer { display: flex; justify-content: space-between; padding-top: 15px; border-top: 1px dashed #888; font-size: 11px; margin-top: 20px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${campusInfo.name}</h2>
            <p>${campusInfo.address}</p>
            <p>Ph: ${campusInfo.phone}</p>
          </div>
          <div class="title">FEE RECEIPT</div>
          <div class="info">
            <div><strong>Receipt No:</strong> ${receipt.receipt_no}</div>
            <div><strong>Date:</strong> ${new Date(receipt.created_at).toLocaleDateString('en-IN')}</div>
            <div><strong>Student:</strong> ${receipt.student_name}</div>
            <div><strong>Class:</strong> ${receipt.class_name}-${receipt.section}</div>
            <div><strong>Roll No:</strong> ${receipt.roll_no}</div>
            <div><strong>Mode:</strong> ${receipt.payment_mode.toUpperCase()}</div>
            <div><strong>Period:</strong> ${receipt.month}</div>
            ${receipt.transaction_id ? `<div><strong>Txn ID:</strong> ${receipt.transaction_id}</div>` : ''}
          </div>
          <table>
            <thead><tr><th>Fee Type</th><th>Amount</th></tr></thead>
            <tbody>
              ${receipt.fee_items.map(i => `<tr><td>${i.fee_type}</td><td>₹${(i.amount || 0).toLocaleString()}</td></tr>`).join('')}
              <tr><td><strong>Total</strong></td><td><strong>₹${receipt.total_amount.toLocaleString()}</strong></td></tr>
              <tr><td>Paid</td><td>₹${receipt.paid_amount.toLocaleString()}</td></tr>
              ${receipt.discount > 0 ? `<tr><td>Discount</td><td>₹${receipt.discount.toLocaleString()}</td></tr>` : ''}
              <tr><td>Balance</td><td>₹${receipt.balance.toLocaleString()}</td></tr>
            </tbody>
          </table>
          <div class="total">Paid Amount: ₹${receipt.paid_amount.toLocaleString()}</div>
          ${receipt.remarks ? `<p><strong>Remarks:</strong> ${receipt.remarks}</p>` : ''}
          <div class="footer">
            <div>Received By: ${receipt.received_by}</div>
            <div>Status: <strong>${receipt.status.toUpperCase()}</strong></div>
            <div>Authorized Signature</div>
          </div>
          <script>window.onload = () => { window.print(); }</script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      showToast("Failed to print receipt", "error");
    }
  };

  return (
    <>
      <div className="filters-row">
        <div className="filter-group">
          <label>Class:</label>
          <select className="filter-select" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} data-testid="fee-deposit-class">
            <option value="">Select</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Section:</label>
          <select className="filter-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} data-testid="fee-deposit-section">
            <option value="">Select</option>
            {sections.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filter-group" style={{ flex: 1, minWidth: "200px" }}>
          <label>Search:</label>
          <input
            type="text"
            className="filter-input"
            style={{ flex: 1 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or roll no"
            data-testid="fee-deposit-search"
          />
        </div>
      </div>

      {students.length > 0 && !selectedStudent && (() => {
        const q = searchQuery.trim().toLowerCase();
        const visible = q
          ? students.filter(s =>
              (s.name || "").toLowerCase().includes(q) ||
              String(s.roll_no || "").toLowerCase().includes(q)
            )
          : students;
        return (
          <div className="tally-table" style={{ marginBottom: "15px" }}>
            <div className="tally-table-inner">
              <div className="tally-table-header" style={{ gridTemplateColumns: "60px 180px 100px 100px" }}>
                <div>Roll</div><div>Name</div><div>Fee Status</div><div>Action</div>
              </div>
              <div className="tally-table-body">
                {visible.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center" }}>No students match "{searchQuery}"</div>
                ) : (
                  visible.map((s) => (
                    <div key={s.id} className="tally-table-row" style={{ gridTemplateColumns: "60px 180px 100px 100px" }}>
                      <div>{s.roll_no}</div>
                      <div>{s.name}</div>
                      <div><span className={`badge ${s.fee_status}`}>{s.fee_status}</span></div>
                      <div>
                        <button className="filter-btn" onClick={() => selectStudent(s)} data-testid={`select-student-${s.id}`}>Select</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {selectedStudent && (
        <div className="form-panel">
          <div className="form-title">
            💰 Fee Deposit - {selectedStudent.name} (Roll: {selectedStudent.roll_no}, Class {selectedStudent.class_name}-{selectedStudent.section})
            <button className="filter-btn" onClick={() => setSelectedStudent(null)} style={{ float: "right" }}>← Back to List</button>
          </div>
          
          <div className="section-header">
            <h3>Select Months (Multi-select for advance payment)</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "5px", marginBottom: "15px" }}>
            {months.map((m) => (
              <label key={m.v} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px", background: selectedMonths.includes(m.v) ? "var(--gold)" : "var(--bg-light)", border: "var(--sunken)", cursor: "pointer", fontSize: "11px" }}>
                <input type="checkbox" checked={selectedMonths.includes(m.v)} onChange={() => toggleMonth(m.v)} />
                {m.n}
              </label>
            ))}
          </div>

          {feeItems.length > 0 ? (
            <>
              <div className="section-header">
                <h3>Fee Details (Editable per student)</h3>
              </div>
              <div className="tally-table">
                <div className="tally-table-header" style={{ gridTemplateColumns: "200px 120px 100px 100px" }}>
                  <div>Fee Type</div><div>Frequency</div><div>Base</div><div>Amount (₹)</div>
                </div>
                <div className="tally-table-body">
                  {feeItems.map((item, idx) => (
                    <div key={idx} className="tally-table-row" style={{ gridTemplateColumns: "200px 120px 100px 100px" }}>
                      <div>{item.fee_type}</div>
                      <div>{item.frequency}</div>
                      <div>₹{(item.original_amount || item.amount).toLocaleString()}</div>
                      <div>
                        <input type="number" value={item.amount} onChange={(e) => updateFeeAmount(idx, e.target.value)} style={{ width: "90%" }} data-testid={`fee-item-amount-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-grid" style={{ marginTop: "15px" }}>
                <div className="form-row"><label>Total Amount:</label><input type="text" value={`₹${totalAmount.toLocaleString()}`} disabled style={{ background: "var(--bg-dark)", fontWeight: "bold" }} /></div>
                <div className="form-row"><label>Paid Amount:</label><input type="number" value={paidAmount} onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)} data-testid="paid-amount" /></div>
                <div className="form-row"><label>Discount:</label><input type="number" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} data-testid="discount" /></div>
                <div className="form-row"><label>Balance:</label><input type="text" value={`₹${Math.max(0, totalAmount - paidAmount - discount).toLocaleString()}`} disabled style={{ background: "var(--bg-dark)", color: (totalAmount - paidAmount - discount > 0 ? "red" : "green") }} /></div>
                <div className="form-row"><label>Payment Mode:</label><select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}><option value="cash">Cash</option><option value="upi">UPI</option><option value="cheque">Cheque</option><option value="neft">NEFT</option></select></div>
                <div className="form-row"><label>Txn ID:</label><input type="text" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="Optional" /></div>
                <div className="form-row" style={{ gridColumn: "1 / -1" }}><label>Remarks:</label><input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} style={{ flex: 1 }} placeholder="Optional" /></div>
                {paidAmount + discount < totalAmount && paidAmount > 0 && (
                  <div className="form-row" style={{ gridColumn: "1 / -1", background: "#fff3cd", padding: "8px" }}>
                    <label><input type="checkbox" checked={markAsPaid} onChange={(e) => setMarkAsPaid(e.target.checked)} data-testid="mark-as-paid" /> Mark as PAID (discount applied - student won't appear in due report)</label>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button className="form-btn secondary" onClick={() => setSelectedStudent(null)}>Cancel</button>
                <button className="form-btn primary" onClick={submitFee} data-testid="submit-fee">💰 Submit Fee & Print Receipt</button>
              </div>
            </>
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "var(--red)" }}>
              No fee structure defined for Class {selectedStudent.class_name}. Please set up Fee Structure first.
            </div>
          )}
        </div>
      )}

      {students.length === 0 && selectedClass && selectedSection && (
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)" }}>
          No students found in Class {selectedClass}-{selectedSection}
        </div>
      )}
    </>
  );
}

// ==================== FEE REPORT PANEL ====================
function FeeReportPanel({ showToast, authHeaders }) {
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [reportType, setReportType] = useState("due");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    if (!selectedClass || !selectedSection) {
      showToast("Please select class and section", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/fee-report?class_name=${selectedClass}&section=${selectedSection}&report_type=${reportType}`, { headers: authHeaders });
      setReportData(res.data);
    } catch (err) {
      showToast("Failed to load report", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedClass && selectedSection) loadReport();
  }, [selectedClass, selectedSection, reportType]);

  const downloadExcel = async () => {
    try {
      const url = `${API}/reports/fee-report/excel?class_name=${selectedClass}&section=${selectedSection}&report_type=${reportType}`;
      const res = await axios.get(url, { headers: authHeaders, responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `fee_report_${selectedClass}_${selectedSection}_${reportType}.xlsx`;
      link.click();
      showToast("Excel downloaded!");
    } catch (err) {
      showToast("Download failed", "error");
    }
  };

  const sendReminder = async (studentId) => {
    if (!window.confirm("Send fee due reminder to this student?")) return;
    try {
      const res = await axios.post(`${API}/communication/fee-due-reminder`, {
        student_ids: [studentId]
      }, { headers: authHeaders });
      showToast(`Reminder: Sent ${res.data.sent}, Skipped ${res.data.skipped}, Failed ${res.data.failed}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Reminder failed", "error");
    }
  };

  const sendAllReminders = async () => {
    if (!selectedClass || !selectedSection) {
      showToast("Select class and section first", "error");
      return;
    }
    if (!window.confirm(`Send fee due reminders to ALL due students in Class ${selectedClass}-${selectedSection}?`)) return;
    try {
      const res = await axios.post(`${API}/communication/fee-due-reminder`, {
        send_all: true,
        class_name: selectedClass,
        section: selectedSection
      }, { headers: authHeaders });
      showToast(`Reminders: Sent ${res.data.sent}, Skipped ${res.data.skipped}, Failed ${res.data.failed}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Reminders failed", "error");
    }
  };

  return (
    <>
      <div className="filters-row">
        <div className="filter-group">
          <label>Class:</label>
          <select className="filter-select" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} data-testid="fee-report-class">
            <option value="">Select</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Section:</label>
          <select className="filter-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} data-testid="fee-report-section">
            <option value="">Select</option>
            <option value="A">A</option><option value="B">B</option><option value="C">C</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Report Type:</label>
          <select className="filter-select" value={reportType} onChange={(e) => setReportType(e.target.value)} data-testid="fee-report-type">
            <option value="due">Due</option>
            <option value="paid">Paid</option>
            <option value="all">All</option>
          </select>
        </div>
        {reportData && (
          <>
            <button className="filter-btn" onClick={downloadExcel} style={{ background: "var(--green)", color: "#fff" }} data-testid="download-excel">
              📥 Download Excel
            </button>
            {reportType === "due" && reportData.records.length > 0 && (
              <button className="filter-btn" onClick={sendAllReminders} style={{ background: "var(--gold-dark)", color: "#fff" }} data-testid="send-all-reminders">
                📢 Send Reminders to All Due
              </button>
            )}
          </>
        )}
      </div>

      {reportData ? (
        <>
          <div className="section-header">
            <h3>Fee Report - Class {reportData.class_name}-{reportData.section} ({reportData.report_type.toUpperCase()})</h3>
          </div>
          <div className="tally-table">
            <div className="tally-table-header" style={{ gridTemplateColumns: "60px 160px 120px 110px 100px 100px 100px 70px 90px" }}>
              <div>Roll</div><div>Name</div><div>Father</div><div>Phone</div><div>Expected</div><div>Paid</div><div>Balance</div><div>Status</div><div>Action</div>
            </div>
            <div className="tally-table-body">
              {reportData.records.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center" }}>No records found for this filter</div>
              ) : (
                reportData.records.map((r) => (
                  <div key={r.student_id} className="tally-table-row" style={{ gridTemplateColumns: "60px 160px 120px 110px 100px 100px 100px 70px 90px" }}>
                    <div>{r.roll_no}</div>
                    <div>{r.name}</div>
                    <div>{r.father_name}</div>
                    <div>{r.phone}</div>
                    <div>₹{(r.expected_to_date || 0).toLocaleString()}</div>
                    <div style={{ color: "var(--green)" }}>₹{r.total_paid.toLocaleString()}</div>
                    <div style={{ color: r.balance_due > 0 ? "var(--red)" : "var(--green)" }}>₹{r.balance_due.toLocaleString()}</div>
                    <div><span className={`badge ${r.status === "paid" ? "paid" : "pending"}`}>{r.status}</span></div>
                    <div>
                      {r.status === "due" && (
                        <button className="att-btn" onClick={() => sendReminder(r.student_id)} title="Send Reminder" data-testid={`send-reminder-${r.student_id}`} style={{ background: "var(--gold-light)", fontSize: "10px", padding: "2px 6px" }}>
                          📢 Remind
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="tally-table-footer">
              <span>Total: {reportData.records.length} records</span>
              <span>All Students: {reportData.total_students}</span>
            </div>
          </div>
        </>
      ) : (
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)" }}>
          Select Class, Section and Report Type to view report
        </div>
      )}
    </>
  );
}

// ==================== ATTENDANCE REPORT PANEL ====================
function AttendanceReportPanel({ showToast, authHeaders }) {
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [reportData, setReportData] = useState(null);
  const monthNames = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

  const loadReport = async () => {
    if (!selectedClass || !selectedSection) return;
    try {
      const res = await axios.get(`${API}/reports/attendance-grid?class_name=${selectedClass}&section=${selectedSection}`, { headers: authHeaders });
      setReportData(res.data);
    } catch (err) {
      showToast("Failed to load report", "error");
    }
  };

  useEffect(() => { if (selectedClass && selectedSection) loadReport(); }, [selectedClass, selectedSection]);

  const downloadExcel = async () => {
    try {
      const url = `${API}/reports/attendance-grid/excel?class_name=${selectedClass}&section=${selectedSection}`;
      const res = await axios.get(url, { headers: authHeaders, responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `attendance_${selectedClass}_${selectedSection}.xlsx`;
      link.click();
      showToast("Excel downloaded!");
    } catch (err) {
      showToast("Download failed", "error");
    }
  };

  return (
    <>
      <div className="filters-row">
        <div className="filter-group">
          <label>Class:</label>
          <select className="filter-select" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} data-testid="att-report-class">
            <option value="">Select</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Section:</label>
          <select className="filter-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} data-testid="att-report-section">
            <option value="">Select</option>
            <option value="A">A</option><option value="B">B</option><option value="C">C</option>
          </select>
        </div>
        {reportData && (
          <button className="filter-btn" onClick={downloadExcel} style={{ background: "var(--green)", color: "#fff" }} data-testid="download-att-excel">
            📥 Download Excel
          </button>
        )}
      </div>

      {reportData ? (
        <>
          <div className="section-header">
            <h3>Attendance Report - Class {reportData.class_name}-{reportData.section} (April to March)</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", background: "var(--white)" }}>
              <thead>
                <tr style={{ background: "var(--navy)", color: "#fff" }}>
                  <th rowSpan="2" style={{ padding: "6px", border: "1px solid #888" }}>Roll</th>
                  <th rowSpan="2" style={{ padding: "6px", border: "1px solid #888" }}>Name</th>
                  {monthNames.map((m) => (
                    <th key={m} colSpan="3" style={{ padding: "6px", border: "1px solid #888" }}>{m}</th>
                  ))}
                  <th colSpan="3" style={{ padding: "6px", border: "1px solid #888", background: "var(--gold-dark)" }}>Total</th>
                </tr>
                <tr style={{ background: "var(--navy-light)", color: "#fff" }}>
                  {[...Array(13)].map((_, i) => (
                    <React.Fragment key={i}>
                      <th style={{ padding: "3px", border: "1px solid #888", color: "#90EE90" }}>P</th>
                      <th style={{ padding: "3px", border: "1px solid #888", color: "#FFB6C1" }}>A</th>
                      <th style={{ padding: "3px", border: "1px solid #888", color: "#87CEEB" }}>L</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.students.map((s) => (
                  <tr key={s.student_id}>
                    <td style={{ padding: "4px", border: "1px solid #ccc", textAlign: "center" }}>{s.roll_no}</td>
                    <td style={{ padding: "4px", border: "1px solid #ccc" }}>{s.name}</td>
                    {reportData.months.map((m) => {
                      const data = s.monthly[m] || { P: 0, A: 0, L: 0 };
                      return (
                        <React.Fragment key={m}>
                          <td style={{ padding: "3px", border: "1px solid #ccc", textAlign: "center", color: "var(--green)" }}>{data.P}</td>
                          <td style={{ padding: "3px", border: "1px solid #ccc", textAlign: "center", color: "var(--red)" }}>{data.A}</td>
                          <td style={{ padding: "3px", border: "1px solid #ccc", textAlign: "center", color: "var(--blue)" }}>{data.L}</td>
                        </React.Fragment>
                      );
                    })}
                    <td style={{ padding: "3px", border: "1px solid #ccc", textAlign: "center", fontWeight: "bold", background: "#d4edda" }}>{s.total_present}</td>
                    <td style={{ padding: "3px", border: "1px solid #ccc", textAlign: "center", fontWeight: "bold", background: "#f8d7da" }}>{s.total_absent}</td>
                    <td style={{ padding: "3px", border: "1px solid #ccc", textAlign: "center", fontWeight: "bold", background: "#cce5ff" }}>{s.total_leave}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-light)" }}>
          Select Class and Section to view report
        </div>
      )}
    </>
  );
}


// ==================== TEST MESSAGE FORM ====================
function TestMessageForm({ authHeaders, showToast }) {
  const [mobile, setMobile] = useState("");
  const [testMessage, setTestMessage] = useState("This is a test message from VIDYALAYA.");
  const [sending, setSending] = useState(false);

  const sendTest = async (channel) => {
    if (!mobile) {
      showToast("Enter mobile number", "error");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/communication/test`, {
        mobile: mobile,
        channel: channel,
        message: testMessage
      }, { headers: authHeaders });
      if (res.data.status === "Sent") {
        showToast(`Test ${channel} sent to ${res.data.to}`);
      } else {
        showToast(`${res.data.status}: ${res.data.reason || res.data.error || "Failed"}`, "error");
      }
    } catch (err) {
      showToast(err.response?.data?.detail || "Test failed", "error");
    }
    setSending(false);
  };

  return (
    <div style={{ marginTop: "15px", padding: "10px", background: "var(--bg-light)", border: "var(--sunken)" }}>
      <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "12px" }}>🧪 Test Messaging (Save settings first)</div>
      <div className="form-grid">
        <div className="form-row">
          <label>Mobile:</label>
          <input type="text" placeholder="9876543210" value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="test-mobile" />
        </div>
        <div className="form-row" style={{ gridColumn: "1 / -1" }}>
          <label>Message:</label>
          <input type="text" value={testMessage} onChange={(e) => setTestMessage(e.target.value)} style={{ flex: 1 }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
        <button className="filter-btn" onClick={() => sendTest("whatsapp")} disabled={sending} data-testid="test-whatsapp">💬 Test WhatsApp</button>
        <button className="filter-btn" onClick={() => sendTest("sms")} disabled={sending} data-testid="test-sms">📱 Test SMS</button>
      </div>
    </div>
  );
}

// ==================== NOTIFICATION HISTORY PANEL ====================
function NotificationHistoryPanel({ showToast, authHeaders }) {
  const classes = ["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    class_name: "",
    section: "",
    notification_type: "",
    channel: "",
    status: ""
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await axios.get(`${API}/communication/logs?${params.toString()}&limit=500`, { headers: authHeaders });
      setLogs(res.data);
    } catch (err) {
      showToast("Failed to load logs", "error");
    }
    setLoading(false);
  };

  useEffect(() => { loadLogs(); }, []);

  const statusBadge = (status) => {
    const map = {
      Delivered: { bg: "#d4edda", color: "#155724" },
      Sent: { bg: "#cce5ff", color: "#004085" },
      Queued: { bg: "#fff3cd", color: "#856404" },
      Failed: { bg: "#f8d7da", color: "#721c24" },
      Skipped: { bg: "#e2e3e5", color: "#383d41" }
    };
    const s = map[status] || { bg: "#eee", color: "#333" };
    return (
      <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: "3px", fontSize: "10px", fontWeight: "bold" }}>
        {status}
      </span>
    );
  };

  return (
    <>
      <div className="filters-row" style={{ flexWrap: "wrap" }}>
        <div className="filter-group">
          <label>From:</label>
          <input type="date" className="filter-input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="notif-date-from" />
        </div>
        <div className="filter-group">
          <label>To:</label>
          <input type="date" className="filter-input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="notif-date-to" />
        </div>
        <div className="filter-group">
          <label>Class:</label>
          <select className="filter-select" value={filters.class_name} onChange={(e) => setFilters({ ...filters, class_name: e.target.value })}>
            <option value="">All</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Section:</label>
          <select className="filter-select" value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })}>
            <option value="">All</option>
            <option value="A">A</option><option value="B">B</option><option value="C">C</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select className="filter-select" value={filters.notification_type} onChange={(e) => setFilters({ ...filters, notification_type: e.target.value })}>
            <option value="">All</option>
            <option value="student_absent">Absent</option>
            <option value="student_leave">Leave</option>
            <option value="fee_payment_received">Fee Payment</option>
            <option value="fee_due">Fee Due</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Channel:</label>
          <select className="filter-select" value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })}>
            <option value="">All</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
            <option value="sms_fallback">SMS (Fallback)</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select className="filter-select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All</option>
            <option value="Delivered">Delivered</option>
            <option value="Sent">Sent</option>
            <option value="Queued">Queued</option>
            <option value="Failed">Failed</option>
            <option value="Skipped">Skipped</option>
          </select>
        </div>
        <button className="filter-btn" onClick={loadLogs} data-testid="notif-search">🔍 Search</button>
      </div>

      <div className="tally-table">
        <div className="tally-table-header" style={{ gridTemplateColumns: "150px 60px 130px 120px 100px 130px 80px" }}>
          <div>Student</div><div>Class</div><div>Mobile</div><div>Type</div><div>Channel</div><div>Date/Time</div><div>Status</div>
        </div>
        <div className="tally-table-body">
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>No notifications found</div>
          ) : (
            logs.map((l) => (
              <div key={l.id} className="tally-table-row" style={{ gridTemplateColumns: "150px 60px 130px 120px 100px 130px 80px" }}>
                <div>{l.student_name || "-"}</div>
                <div>{l.class_name ? `${l.class_name}-${l.section || ""}` : "-"}</div>
                <div>{l.parent_mobile || l.parent_mobile_raw || "-"}</div>
                <div style={{ fontSize: "10px" }}>{l.notification_type}</div>
                <div>{l.channel || "-"}</div>
                <div style={{ fontSize: "10px" }}>
                  {l.triggered_at ? new Date(l.triggered_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : "-"}
                </div>
                <div>{statusBadge(l.status)}</div>
              </div>
            ))
          )}
        </div>
        <div className="tally-table-footer">
          <span>Total: {logs.length} notifications</span>
          <span>
            Delivered: {logs.filter(l => l.status === "Delivered").length} |
            Sent: {logs.filter(l => l.status === "Sent").length} |
            Failed: {logs.filter(l => l.status === "Failed").length} |
            Skipped: {logs.filter(l => l.status === "Skipped").length}
          </span>
        </div>
      </div>
    </>
  );
}

// ==================== MODAL MANAGER ====================
function ModalManager({ modal, onClose, students, fees, showToast, authHeaders, onRefresh }) {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (modal.type === "editStudent" && modal.student) {
      // Pre-fill form with existing student data
      setFormData({
        name: modal.student.name || "",
        class_name: modal.student.class_name || "",
        section: modal.student.section || "",
        roll_no: modal.student.roll_no || "",
        father_name: modal.student.father_name || "",
        mother_name: modal.student.mother_name || "",
        phone: modal.student.phone || "",
        email: modal.student.email || "",
        address: modal.student.address || ""
      });
    } else if (modal.type === "addStudent" && modal.prefill) {
      // Pre-fill Class and Section from filters if available
      setFormData({
        class_name: modal.prefill.class_name || "",
        section: modal.prefill.section || ""
      });
    } else if (modal.type === "changePassword") {
      setFormData({ current_password: "", new_password: "", confirm_password: "" });
    } else {
      setFormData({});
    }
  }, [modal]);

  const handleAddStudent = async () => {
    try {
      await axios.post(`${API}/students`, formData, { headers: authHeaders });
      showToast("Student added successfully!");
      onClose();
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Add failed", "error");
    }
  };

  const handleEditStudent = async () => {
    try {
      await axios.put(`${API}/students/${modal.student.id}`, formData, { headers: authHeaders });
      showToast("Student updated successfully!");
      onClose();
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", "error");
    }
  };

  const handleAddFee = async () => {
    try {
      await axios.post(`${API}/fees`, formData, { headers: authHeaders });
      showToast("Fee created successfully!");
      onClose();
      onRefresh();
    } catch (err) {
      showToast(err.response?.data?.detail || "Add failed", "error");
    }
  };

  const handleChangePassword = async () => {
    if (!formData.current_password || !formData.new_password) {
      showToast("Enter current and new password", "error");
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      showToast("New password and confirmation do not match", "error");
      return;
    }
    if (formData.new_password.length < 4) {
      showToast("New password must be at least 4 characters", "error");
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password,
      }, { headers: authHeaders });
      showToast("Password updated successfully!");
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || "Password change failed", "error");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-window" onClick={(e) => e.stopPropagation()} style={{ width: "550px" }}>
        <div className="modal-title-bar">
          <span>
            {modal.type === "addStudent" && "➕ Add New Student"}
            {modal.type === "editStudent" && "✏️ Edit Student Details"}
            {modal.type === "addFee" && "➕ Create Fee Entry"}
            {modal.type === "changePassword" && "🔑 Change Password"}
          </span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-content">
          {(modal.type === "addStudent" || modal.type === "editStudent") && (
            <div className="form-grid">
              <div className="form-row">
                <label>Name:</label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="modal-student-name"
                />
              </div>
              <div className="form-row">
                <label>Class:</label>
                <select
                  value={formData.class_name || ""}
                  onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                >
                  <option value="">Select</option>
                  {["Nursery", "LKG", "UKG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Section:</label>
                <select
                  value={formData.section || ""}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
              <div className="form-row">
                <label>Roll No:</label>
                <input
                  type="text"
                  value={formData.roll_no || ""}
                  onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Father Name:</label>
                <input
                  type="text"
                  value={formData.father_name || ""}
                  onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Mother Name:</label>
                <input
                  type="text"
                  value={formData.mother_name || ""}
                  onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Phone:</label>
                <input
                  type="text"
                  value={formData.phone || ""}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="modal-student-phone"
                />
              </div>
              <div className="form-row">
                <label>Email:</label>
                <input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-row" style={{ gridColumn: "1 / -1" }}>
                <label>Address:</label>
                <input
                  type="text"
                  value={formData.address || ""}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          )}

          {modal.type === "addFee" && (
            <div className="form-grid">
              <div className="form-row">
                <label>Student:</label>
                <select
                  value={formData.student_id || ""}
                  onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                  data-testid="modal-fee-student"
                >
                  <option value="">Select Student</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.class_name}-{s.section})</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Fee Type:</label>
                <select
                  value={formData.fee_type || ""}
                  onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="tuition">Tuition</option>
                  <option value="transport">Transport</option>
                  <option value="exam">Exam</option>
                  <option value="admission">Admission</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-row">
                <label>Amount:</label>
                <input
                  type="number"
                  value={formData.amount || ""}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  data-testid="modal-fee-amount"
                />
              </div>
              <div className="form-row">
                <label>Due Date:</label>
                <input
                  type="date"
                  value={formData.due_date || ""}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Year:</label>
                <input
                  type="text"
                  value={formData.academic_year || "2026-27"}
                  onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                />
              </div>
            </div>
          )}

          {modal.type === "changePassword" && (
            <div className="form-grid">
              <div className="form-row" style={{ gridColumn: "1/-1" }}>
                <label>Current Password:</label>
                <input
                  type="password"
                  value={formData.current_password || ""}
                  onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                  data-testid="modal-current-password"
                  style={{ flex: 1 }}
                />
              </div>
              <div className="form-row" style={{ gridColumn: "1/-1" }}>
                <label>New Password:</label>
                <input
                  type="password"
                  value={formData.new_password || ""}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  data-testid="modal-new-password"
                  style={{ flex: 1 }}
                />
              </div>
              <div className="form-row" style={{ gridColumn: "1/-1" }}>
                <label>Confirm Password:</label>
                <input
                  type="password"
                  value={formData.confirm_password || ""}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  data-testid="modal-confirm-password"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button className="form-btn secondary" onClick={onClose}>Cancel</button>
            <button
              className="form-btn primary"
              onClick={() => {
                if (modal.type === "addStudent") handleAddStudent();
                if (modal.type === "editStudent") handleEditStudent();
                if (modal.type === "addFee") handleAddFee();
                if (modal.type === "changePassword") handleChangePassword();
              }}
              data-testid="modal-submit"
            >
              {modal.type === "addStudent" && "Add Student"}
              {modal.type === "editStudent" && "Update Student"}
              {modal.type === "addFee" && "Create Fee"}
              {modal.type === "changePassword" && "Update Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN APP COMPONENT ====================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="login-overlay">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return user ? (
    <MainApp user={user} onLogout={handleLogout} />
  ) : (
    <LoginScreen onLogin={handleLogin} />
  );
}

export default App;
